import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { vibrate } from "../utils/haptics";
import {
  Home, BookOpen, Compass, AlertTriangle, Wifi, Phone, MessageCircle,
  ChevronRight, ChevronDown, MapPin, Copy, Check, X, ExternalLink, Globe,
  Thermometer, Droplets, Tv, Lock, Car, Trash2, ShieldAlert,
  Coffee, Utensils, Bed, Bath, Wind, Flame, Zap, Key,
  Sun, Moon, Camera, Music, Shirt, Package, Heart, Star, Info,
  Navigation, Clock, DollarSign, ShoppingBag, Bike, Wine, LogOut, Search,
  Volume2, CreditCard, Gift, Bell, Scissors, Wrench, Dumbbell, Waves,
  Mountain, Luggage, Smartphone, Bus, Users, Send, Sparkles, Loader, Wand2,
  TrainFront, Ticket, Droplet, Signal, BookMarked, ArrowUpRight, Siren, Mic, ChevronLeft, FileText, Download, Plus
} from "lucide-react";
import OneSignal from "react-onesignal";
import WeatherWidget from "../components/WeatherWidget";
import ToiletsMap from "../components/ToiletsMap";
import StayInTouch from "../components/StayInTouch";
import VoiceRecorder from "../components/VoiceRecorder";
import EventsTicker from "../components/EventsTicker";
import { parseVideoUrl } from "../utils/videoUrl";
import LazyImage from "../components/LazyImage";
import BottomSheet from "../components/BottomSheet";
import LanguageSwitcher from "../components/LanguageSwitcher";
import TornaACasa from "../components/TornaACasa";
import WeatherMicroTile from "../components/WeatherMicroTile";
import WeatherDetail from "../components/WeatherDetail";
import TicketSheet from "../components/TicketSheet";
import "../components/TechSheet.css";
import GuestPromos from "../components/GuestPromos";
import { BadgePercent } from "lucide-react";
// WeatherAlertBanner import rimosso (2026-09): alert meteo avverso disabilitato.
import PlaceDetailSheet from "../components/PlaceDetailSheet";
import { TRANSPORT_CATEGORIES, TRANSPORTS_ACCENT, TRANSPORTS_TITLE, TRANSPORTS_SUBTITLE, TRANSPORT_ACTIONS, getCategory } from "../data/transports";
import { LUGGAGE_ACCENT, LUGGAGE_TITLE, LUGGAGE_SUBTITLE, LUGGAGE_LABELS, fmtDistance } from "../data/luggage";
import { CUSTOM_ICONS } from "../data/customButtonIcons";
import { getPreset, tileGradient } from "../data/themePresets";
import { StoriesRow, StoriesViewer } from "../components/StoriesViewer";
import { STORIES_TITLE, groupStories } from "../data/stories";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Photo/media reference can be either an internal file id (served via /api/files)
// or a full external URL (es. immagini demo). Resolve both transparently.
const fileSrc = (ref) => (/^https?:\/\//.test(ref || "") ? ref : `${API}/files/${ref}`);

const USEFUL_ICON_MAP = {
  taxi: { icon: Car, color: "#F59E0B" }, bus: { icon: Bus, color: "#3B82F6" },
  bike_rental: { icon: Bike, color: "#10B981" }, scooter_rental: { icon: Bike, color: "#8B5CF6" },
  car_rental: { icon: Car, color: "#EC4899" }, ncc: { icon: Car, color: "#1E293B" },
  ski_rental: { icon: Mountain, color: "#06B6D4" }, generic_rental: { icon: Package, color: "#F97316" },
  laundry: { icon: Shirt, color: "#14B8A6" }, mechanic: { icon: Wrench, color: "#64748B" },
  tour_guide: { icon: Compass, color: "#D946EF" }, babysitter: { icon: Heart, color: "#F43F5E" },
  dogsitter: { icon: Heart, color: "#A855F7" }, tire_shop: { icon: Wrench, color: "#78716C" },
  luggage: { icon: Luggage, color: "#0EA5E9" }, spa: { icon: Droplets, color: "#E879F9" },
  hairdresser: { icon: Scissors, color: "#F472B6" }, phone_repair: { icon: Smartphone, color: "#6366F1" },
  gym: { icon: Dumbbell, color: "#EF4444" }, ski_school: { icon: Mountain, color: "#2563EB" },
  pool: { icon: Waves, color: "#0891B2" }, barber: { icon: Scissors, color: "#92400E" },
  supermarket: { icon: ShoppingBag, color: "#16A34A" },
};

const extractYouTubeId = (url) => {
  if (!url) return "";
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : "";
};

/* ─── Typewriter Effect ─── */
const Typewriter = ({ text, className }) => {
  const [displayed, setDisplayed] = useState("");
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    setDisplayed("");
    const chars = text.split("");
    const timers = chars.map((_, i) =>
      setTimeout(() => setDisplayed(text.slice(0, i + 1)), 30 + i * 45)
    );
    return () => timers.forEach(clearTimeout);
  }, [started, text]);
  return (
    <span ref={ref} className={className}>
      {displayed || "\u00A0"}
      {started && displayed.length < text.length && (
        <span className="inline-block w-[2px] h-[0.85em] bg-current ml-0.5 align-middle animate-pulse" />
      )}
    </span>
  );
};

// ═══ TRANSLATIONS ═══
const T = {
  it: {
    welcome: "Benvenuto",
    welcomesYou: "vi dà il benvenuto",
    wifi: "Wi-Fi",
    network: "Rete",
    networks: "reti",
    password: "Password",
    copyPassword: "Copia Password",
    copied: "Copiata!",
    homeManuals: "Manuali Casa",
    explore: "Esplora",
    extraServices: "Servizi Extra",
    emergencies: "Emergenze",
    manuals: "Manuali",
    help: "Aiuto",
    noManuals: "Nessun manuale configurato",
    recommendedPlaces: "Posti Consigliati",
    extraServicesLabel: "Servizi Extra",
    extraServicesDisclaimer: "Solo per strutture autorizzate a fornire servizi extra.",
    wasteCollection: "Raccolta Rifiuti",
    noContent: "Nessun contenuto",
    contactHost: "Contatta l'Host",
    call: "Chiama",
    emergencyAddress: "Indirizzo da comunicare al 112",
    openMaps: "Apri in Google Maps",
    request: "Richiedi",
    onRequest: "Su richiesta",
    call: "Chiama",
    discountReserved: "riservato",
    discountInstruction: "Per ottenere lo sconto, comunica:",
    guestOfProperty: "ospite della struttura",
    notAvailable: "GuestBook non disponibile",
    rules: "Regole",
    houseRules: "Regole della Casa",
    checkin: "Check-in",
    checkout: "Check-out",
    quietHours: "Orari di Silenzio",
    from: "dalle",
    to: "alle",
    whereToLeaveKey: "Dove lasciare la chiave",
    touristTax: "Tassa di Soggiorno",
    payNow: "Paga Ora",
    tipTitle: "Ti è piaciuto il soggiorno?",
    tipBtn: "Lascia una mancia",
    directBooking: "Torna da noi, chiamaci direttamente!",
    directBookingDesc: "Ti facciamo lo sconto. Prenota diretto, senza commissioni.",
    viewProperty: "Vedi e Prenota",
    recommendedBadge: "Consigliato",
    recommendedTitle: "Consigliato dal tuo host",
    recommendedHint: "I posti con la stella dorata ⭐ sono i preferiti del tuo host: prova questi prima di tutti gli altri.",
  },
  en: {
    welcome: "Welcome",
    welcomesYou: "welcomes you",
    wifi: "Wi-Fi",
    network: "Network",
    networks: "networks",
    password: "Password",
    copyPassword: "Copy Password",
    copied: "Copied!",
    homeManuals: "House Manuals",
    explore: "Explore",
    extraServices: "Extra Services",
    emergencies: "Emergencies",
    manuals: "Manuals",
    help: "Help",
    noManuals: "No manuals configured",
    recommendedPlaces: "Recommended Places",
    extraServicesLabel: "Extra Services",
    extraServicesDisclaimer: "Only for authorised properties providing extra services.",
    wasteCollection: "Waste Collection",
    noContent: "No content",
    contactHost: "Contact Host",
    call: "Call",
    emergencyAddress: "Address to provide to 112",
    openMaps: "Open in Google Maps",
    request: "Request",
    onRequest: "On request",
    call: "Call",
    discountReserved: "reserved",
    discountInstruction: "To get the discount, say:",
    guestOfProperty: "guest of",
    notAvailable: "GuestBook not available",
    rules: "Rules",
    houseRules: "House Rules",
    checkin: "Check-in",
    checkout: "Check-out",
    quietHours: "Quiet Hours",
    from: "from",
    to: "to",
    whereToLeaveKey: "Where to leave the key",
    touristTax: "Tourist Tax",
    payNow: "Pay Now",
    tipTitle: "Did you enjoy your stay?",
    tipBtn: "Leave a tip",
    directBooking: "Come back to us, call us directly!",
    directBookingDesc: "We'll give you a discount. Book direct, no commissions.",
    viewProperty: "View & Book",
    recommendedBadge: "Recommended",
    recommendedTitle: "Recommended by your host",
    recommendedHint: "Places marked with a gold star ⭐ are your host's personal favourites — try these first.",
  },
  fr: {
    welcome: "Bienvenue",
    welcomesYou: "vous souhaite la bienvenue",
    wifi: "Wi-Fi",
    network: "Réseau",
    networks: "réseaux",
    password: "Mot de passe",
    copyPassword: "Copier le mot de passe",
    copied: "Copié !",
    homeManuals: "Manuels Maison",
    explore: "Explorer",
    extraServices: "Services Extra",
    emergencies: "Urgences",
    manuals: "Manuels",
    help: "Aide",
    noManuals: "Aucun manuel configuré",
    recommendedPlaces: "Lieux Recommandés",
    extraServicesLabel: "Services Extra",
    extraServicesDisclaimer: "Uniquement pour les structures autorisées.",
    wasteCollection: "Collecte des Déchets",
    noContent: "Aucun contenu",
    contactHost: "Contacter l'Hôte",
    call: "Appeler",
    emergencyAddress: "Adresse à communiquer au 112",
    openMaps: "Ouvrir dans Google Maps",
    request: "Demander",
    onRequest: "Sur demande",
    call: "Appeler",
    discountReserved: "réservé",
    discountInstruction: "Pour obtenir la réduction, dites :",
    guestOfProperty: "hôte de",
    notAvailable: "GuestBook non disponible",
    rules: "Règles",
    houseRules: "Règles de la Maison",
    checkin: "Arrivée",
    checkout: "Départ",
    quietHours: "Heures de Silence",
    from: "de",
    to: "à",
    whereToLeaveKey: "Où laisser la clé",
    touristTax: "Taxe de Séjour",
    payNow: "Payer Maintenant",
    tipTitle: "Vous avez apprécié votre séjour ?",
    tipBtn: "Laisser un pourboire",
    directBooking: "Revenez chez nous, appelez-nous directement !",
    directBookingDesc: "On vous fait une réduction. Réservez en direct, sans commissions.",
    viewProperty: "Voir et Réserver",
    recommendedBadge: "Recommandé",
    recommendedTitle: "Recommandé par votre hôte",
    recommendedHint: "Les lieux marqués d'une étoile dorée ⭐ sont les favoris de votre hôte — essayez-les en priorité.",
  },
  es: {
    welcome: "Bienvenido",
    welcomesYou: "os da la bienvenida",
    wifi: "Wi-Fi",
    network: "Red",
    networks: "redes",
    password: "Contraseña",
    copyPassword: "Copiar Contraseña",
    copied: "¡Copiada!",
    homeManuals: "Manuales Casa",
    explore: "Explorar",
    extraServices: "Servicios Extra",
    emergencies: "Emergencias",
    manuals: "Manuales",
    help: "Ayuda",
    noManuals: "Ningún manual configurado",
    recommendedPlaces: "Lugares Recomendados",
    extraServicesLabel: "Servicios Extra",
    extraServicesDisclaimer: "Solo para estructuras autorizadas.",
    wasteCollection: "Recogida de Residuos",
    noContent: "Sin contenido",
    contactHost: "Contactar Host",
    call: "Llamar",
    emergencyAddress: "Dirección para comunicar al 112",
    openMaps: "Abrir en Google Maps",
    request: "Solicitar",
    onRequest: "Bajo petición",
    call: "Llamar",
    discountReserved: "reservado",
    discountInstruction: "Para obtener el descuento, di:",
    guestOfProperty: "huésped de",
    notAvailable: "GuestBook no disponible",
    rules: "Reglas",
    houseRules: "Reglas de la Casa",
    checkin: "Entrada",
    checkout: "Salida",
    quietHours: "Horas de Silencio",
    from: "de",
    to: "a",
    whereToLeaveKey: "Dónde dejar la llave",
    touristTax: "Impuesto Turístico",
    payNow: "Pagar Ahora",
    tipTitle: "¿Te gustó tu estancia?",
    tipBtn: "Dejar propina",
    directBooking: "¡Vuelve con nosotros, llámanos directamente!",
    directBookingDesc: "Te hacemos descuento. Reserva directo, sin comisiones.",
    viewProperty: "Ver y Reservar",
    recommendedBadge: "Recomendado",
    recommendedTitle: "Recomendado por tu anfitrión",
    recommendedHint: "Los lugares con estrella dorada ⭐ son los favoritos de tu anfitrión: pruébalos primero.",
  },
  de: {
    welcome: "Willkommen",
    welcomesYou: "heißt euch willkommen",
    wifi: "Wi-Fi",
    network: "Netzwerk",
    networks: "Netzwerke",
    password: "Passwort",
    copyPassword: "Passwort kopieren",
    copied: "Kopiert!",
    homeManuals: "Hausanleitungen",
    explore: "Entdecken",
    extraServices: "Extra-Services",
    emergencies: "Notfälle",
    manuals: "Anleitungen",
    help: "Hilfe",
    noManuals: "Keine Anleitungen konfiguriert",
    recommendedPlaces: "Empfohlene Orte",
    extraServicesLabel: "Extra-Services",
    extraServicesDisclaimer: "Nur für autorisierte Unterkünfte.",
    wasteCollection: "Abfallsammlung",
    noContent: "Kein Inhalt",
    contactHost: "Host kontaktieren",
    call: "Anrufen",
    emergencyAddress: "Adresse für die 112 angeben",
    openMaps: "In Google Maps öffnen",
    request: "Anfragen",
    onRequest: "Auf Anfrage",
    discountReserved: "reserviert",
    discountInstruction: "Um den Rabatt zu erhalten, sage:",
    guestOfProperty: "Gast von",
    notAvailable: "GuestBook nicht verfügbar",
    rules: "Regeln",
    houseRules: "Hausordnung",
    checkin: "Check-in",
    checkout: "Check-out",
    quietHours: "Ruhezeiten",
    from: "von",
    to: "bis",
    whereToLeaveKey: "Wo den Schlüssel zurücklassen",
    touristTax: "Kurtaxe",
    payNow: "Jetzt zahlen",
    tipTitle: "Hat dir dein Aufenthalt gefallen?",
    tipBtn: "Trinkgeld geben",
    directBooking: "Komm zurück, ruf uns direkt an!",
    directBookingDesc: "Wir geben dir einen Rabatt. Direkt buchen, ohne Provision.",
    viewProperty: "Ansehen & Buchen",
    recommendedBadge: "Empfohlen",
    recommendedTitle: "Vom Gastgeber empfohlen",
    recommendedHint: "Mit goldenem Stern ⭐ markierte Orte sind die Favoriten deines Gastgebers — probiere sie zuerst.",
  },
};

const FLAGS = [
  { code: "it", flag: "🇮🇹", abbr: "IT" },
  { code: "en", flag: "🇬🇧", abbr: "EN" },
  { code: "fr", flag: "🇫🇷", abbr: "FR" },
  { code: "es", flag: "🇪🇸", abbr: "ES" },
  { code: "de", flag: "🇩🇪", abbr: "DE" },
];

// ═══ ICON MAP ═══
const ICON_MAP = {
  thermometer: Thermometer, droplets: Droplets, tv: Tv, lock: Lock, car: Car,
  trash: Trash2, coffee: Coffee, utensils: Utensils, bed: Bed, bath: Bath,
  wind: Wind, flame: Flame, zap: Zap, key: Key, sun: Sun, moon: Moon,
  camera: Camera, music: Music, shirt: Shirt, package: Package, heart: Heart,
  star: Star, info: Info, wifi: Wifi, phone: Phone, alert: AlertTriangle,
  shield: ShieldAlert, map: MapPin, navigation: Navigation, clock: Clock,
  dollar: DollarSign, shopping: ShoppingBag, bike: Bike, wine: Wine, logout: LogOut
};

// ═══ SPRING ═══
const spring = { type: "spring", stiffness: 260, damping: 22 };
const cardIn = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: (i) => ({ opacity: 1, y: 0, scale: 1, transition: { ...spring, delay: i * 0.07 } })
};
const iconPulse = { scale: [1, 1.15, 1], transition: { duration: 1.8, repeat: Infinity, ease: "easeInOut" } };

// ═══ EMERGENCY CONTACTS ═══
const EMERGENCY = [
  { name: "Emergenza Unica", num: "112", color: "#EF4444" },
  { name: "Vigili del Fuoco", num: "115", color: "#F97316" },
  { name: "Ambulanza", num: "118", color: "#22C55E" },
  { name: "Polizia", num: "113", color: "#3B82F6" },
  { name: "Carabinieri", num: "112", color: "#1E40AF" },
  { name: "Guardia Costiera", num: "1530", color: "#06B6D4" },
];

// ═══ WIFI CARD ═══
// Same visual pattern as the Check-in card so the guestbook feels like ONE app:
// outer white rounded card, tinted header strip with icon square, body inside.
//
// Supports up to 5 networks (config.wifi.networks). Falls back to the legacy
// single-network schema (wifi.ssid / wifi.password / wifi.encryption) when
// no ``networks`` array is provided, so previously-published Welcome Books
// keep working without any migration.
const collectNetworks = (wifi) => {
  if (!wifi) return [];
  const arr = Array.isArray(wifi.networks) ? wifi.networks : [];
  const cleaned = arr
    .map((n) => ({
      label: (n?.label || "").trim(),
      ssid: (n?.ssid || "").trim(),
      password: n?.password || "",
      encryption: n?.encryption || "WPA2",
    }))
    .filter((n) => n.ssid);
  if (cleaned.length > 0) return cleaned.slice(0, 5);
  if (wifi.ssid) {
    return [{
      label: "",
      ssid: wifi.ssid,
      password: wifi.password || "",
      encryption: wifi.encryption || "WPA2",
    }];
  }
  return [];
};

const WifiNetworkBlock = ({ net, idx, t, isMulti }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!net.password) return;
    navigator.clipboard.writeText(net.password);
    setCopied(true);
    toast.success(t.copied);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="space-y-2.5" data-testid={`wifi-net-${idx}`}>
      {isMulti && (<div className="tk-label">{net.label || `Wi-Fi ${idx + 1}`}</div>)}
      <div className="tk-codebox">
        <div className="min-w-0">
          <div className="cb-lbl">{t.network}</div>
          <div className="cb-val" style={{ fontSize: "16px", letterSpacing: ".04em" }}>{net.ssid}</div>
        </div>
        <div className="tk-bars"><i /><i /><i /><i /></div>
      </div>
      <div className="tk-codebox">
        <div className="min-w-0">
          <div className="cb-lbl">{t.password}</div>
          <div className="cb-val">{net.password || "—"}</div>
        </div>
        <button className="tk-copy" onClick={copy} data-testid={`wifi-copy-btn-${idx}`} aria-label={t.copyPassword} style={copied ? { background: "#34d399" } : {}}>
          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

const SAILY_URL = "https://go.saily.site/SH16M";
const SAILY_TXT = {
  it: { t: "Dati sempre attivi in giro per la città", s: "Attiva la eSIM Saily con il 15% di sconto: internet dedicato quando esci, senza cambiare SIM.", c: "Attiva con il 15% di sconto" },
  en: { t: "Stay online all around the city", s: "Get the Saily eSIM with 15% off: dedicated data when you're out, no SIM swap needed.", c: "Get 15% off" },
  fr: { t: "Toujours connecté en ville", s: "Active l'eSIM Saily avec 15% de réduction : data dédiée en balade, sans changer de SIM.", c: "Profiter des -15%" },
  es: { t: "Siempre conectado por la ciudad", s: "Activa la eSIM Saily con un 15% de descuento: datos dedicados cuando sales, sin cambiar de SIM.", c: "Activar con 15% dto." },
  de: { t: "Immer online in der ganzen Stadt", s: "Hol dir die Saily eSIM mit 15% Rabatt: eigene Daten unterwegs, ohne SIM-Wechsel.", c: "15% Rabatt sichern" },
};

const SailyBanner = ({ lang }) => {
  const x = SAILY_TXT[lang] || SAILY_TXT.it;
  return (
    <a
      href={SAILY_URL}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="block rounded-2xl overflow-hidden border border-amber-300 bg-gradient-to-br from-amber-300 to-amber-400 shadow-sm active:scale-[0.99] transition-transform"
      data-testid="saily-promo-banner"
    >
      <div className="p-4 flex items-center gap-3">
        <div className="shrink-0 flex items-center gap-1.5 bg-white rounded-xl px-3 py-2 shadow-sm">
          <Globe className="w-4 h-4 text-amber-600" />
          <span className="font-black tracking-tight text-zinc-900 text-sm">Saily</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-black text-zinc-900 leading-tight">{x.t}</p>
          <p className="text-[11px] text-zinc-800/80 font-semibold leading-snug mt-0.5">{x.s}</p>
        </div>
      </div>
      <div className="px-4 pb-3">
        <span className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-[12px] font-black rounded-full px-4 py-2">
          {x.c} <ExternalLink className="w-3.5 h-3.5" />
        </span>
      </div>
    </a>
  );
};

const HOSTLAND_HOME = "https://hostland.vitadahost.it/";
const MADE_WITH = {
  it: "Questo spazio pazzesco è realizzato con Hostland",
  en: "This amazing space is made with Hostland",
  fr: "Cet espace incroyable est réalisé avec Hostland",
  es: "Este increíble espacio está hecho con Hostland",
  de: "Dieser großartige Raum wurde mit Hostland erstellt",
};

const MadeWithHostland = ({ lang, propertyId }) => {
  const track = () => {
    if (propertyId) axios.post(`${API}/guestbook/${propertyId}/referral-click`).catch(() => {});
  };
  return (
    <a
      href={`${HOSTLAND_HOME}?ref=welcomebook`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={track}
      className="mt-3 flex w-fit mx-auto items-center gap-1.5 bg-white border border-zinc-200 rounded-full px-3 py-1.5 shadow-sm active:scale-[0.98] transition-transform"
      data-testid="made-with-hostland"
    >
      <img src="/hostland-logo.png" alt="Hostland" className="w-4 h-4 rounded object-cover shrink-0" />
      <p className="text-[10px] font-semibold text-zinc-900 leading-none">
        {MADE_WITH[lang] || MADE_WITH.it}
      </p>
      <ArrowUpRight className="w-3 h-3 text-zinc-500 shrink-0" strokeWidth={2.2} />
    </a>
  );
};

const WifiCard = ({ wifi, t, lang, accent = "#4F46E5", gradient = "linear-gradient(135deg,#6366F1 0%,#4F46E5 55%,#3730A3 100%)" }) => {
  const networks = collectNetworks(wifi);
  if (networks.length === 0) return null;
  const isMulti = networks.length > 1;
  const primary = networks[0];
  const sailyOn = wifi?.saily_promo_enabled !== false; // attivo di default
  return (
    <motion.div variants={cardIn} custom={1} className="tk" style={{ "--tk-accent": accent, "--tk-accent-soft": `${accent}2e` }}>
      <div className="tk-checker" />
      <div className="tk-head tk-head-vibrant" style={{ background: gradient }}>
        <div className="tk-head-row">
          <div className="tk-ic"><Wifi className="w-5 h-5" strokeWidth={2.4} /></div>
          <div>
            <div className="tk-kicker"><span className="dot" />{isMulti ? `${networks.length} ${t.networks || "reti"}` : (primary.encryption || "WPA2")}</div>
            <div className="tk-title">Wi-Fi</div>
          </div>
        </div>
        <div className="tk-sub">{lang === "it" ? "Connettiti in un tap" : lang === "fr" ? "Connecte-toi en un tap" : lang === "es" ? "Conéctate en un toque" : lang === "de" ? "Mit einem Tap verbinden" : "Connect in one tap"}</div>
      </div>
      <div className="tk-perf"><span /></div>
      <div className="tk-body space-y-4">
        {networks.map((n, i) => (
          <WifiNetworkBlock key={`${n.ssid}-${i}`} net={n} idx={i} t={t} isMulti={isMulti} />
        ))}
        {sailyOn && <SailyBanner lang={lang} />}
      </div>
    </motion.div>
  );
};

// ═══ MANUAL CARD ═══
const ManualCard = ({ manual, index }) => {
  const [open, setOpen] = useState(false);
  const IconComp = ICON_MAP[manual.icon] || Info;
  return (
    <motion.div variants={cardIn} custom={index} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-zinc-100">
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex items-center gap-4 text-left">
        <motion.div animate={!open ? iconPulse : {}} className="w-12 h-12 bg-zinc-50 rounded-2xl flex items-center justify-center shrink-0">
          <IconComp className="w-6 h-6 text-zinc-700" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{manual.title}</p>
          {manual.description && <p className="text-xs text-zinc-400 mt-0.5 truncate">{manual.description}</p>}
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-300 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
            <div className="px-6 pb-5 space-y-4">
              {manual.media_url && (
                <div className="rounded-2xl overflow-hidden bg-zinc-50">
                  {manual.media_type === "video" ? (
                    <video src={`${API}/files/${manual.media_url}`} controls className="w-full rounded-2xl" playsInline />
                  ) : (
                    <img src={`${API}/files/${manual.media_url}`} alt={manual.title} className="w-full rounded-2xl object-cover max-h-52" />
                  )}
                </div>
              )}
              {manual.content && <p className="text-[15px] text-zinc-600 leading-relaxed whitespace-pre-line">{manual.content}</p>}
              {manual.faqs?.length > 0 && (
                <div className="space-y-2">
                  {manual.faqs.map((faq, i) => (
                    <div key={i} className="bg-amber-50 rounded-2xl p-4">
                      <p className="text-xs font-bold text-amber-700 mb-1">{faq.question}</p>
                      <p className="text-xs text-amber-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ═══ PASTEL PALETTE — deterministic color per category ═══
// Soft, low-saturation pastels that stay airy and never compete with photos.
const PASTEL_PALETTE = [
  { bg: "#FEF3C7", icon: "#D97706" }, // amber-100 / amber-600
  { bg: "#DBEAFE", icon: "#2563EB" }, // blue-100  / blue-600
  { bg: "#FCE7F3", icon: "#DB2777" }, // pink-100  / pink-600
  { bg: "#D1FAE5", icon: "#059669" }, // emerald-100 / emerald-600
  { bg: "#EDE9FE", icon: "#7C3AED" }, // violet-100 / violet-600
  { bg: "#FFEDD5", icon: "#EA580C" }, // orange-100 / orange-600
  { bg: "#CFFAFE", icon: "#0891B2" }, // cyan-100 / cyan-600
  { bg: "#FEE2E2", icon: "#DC2626" }, // red-100 / red-600
  { bg: "#E0E7FF", icon: "#4F46E5" }, // indigo-100 / indigo-600
  { bg: "#FEF9C3", icon: "#CA8A04" }, // yellow-100 / yellow-600
  { bg: "#F3E8FF", icon: "#9333EA" }, // purple-100 / purple-600
  { bg: "#CCFBF1", icon: "#0D9488" }, // teal-100 / teal-600
];

const pastelForCategory = (cat) => {
  if (!cat) return PASTEL_PALETTE[0];
  let h = 0;
  for (let i = 0; i < cat.length; i++) h = (h * 31 + cat.charCodeAt(i)) >>> 0;
  return PASTEL_PALETTE[h % PASTEL_PALETTE.length];
};

// ═══ PLACE CARD (compact horizontal) ═══
const PlaceCard = ({ place, index, propertyName, t, onClick, pastel }) => {
  const tone = pastel || pastelForCategory(place.category || place.name);
  return (
    <motion.div
      variants={cardIn} custom={index}
      whileTap={{ scale: 0.97 }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      className="min-w-[240px] max-w-[280px] rounded-[28px] overflow-hidden shrink-0 cursor-pointer snap-start"
      style={{
        background: tone.bg,
        boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 8px 24px -8px rgba(0,0,0,0.08)",
      }}
    >
      {place.image_url ? (
        <div className="relative">
          <LazyImage
            src={place.image_url.startsWith("http") ? place.image_url : `${API}/files/${place.image_url}`}
            alt={place.name}
            className="w-full h-40"
            width={280}
            eager={index < 3}
          />
          {place.host_recommended && (
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-400 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shadow-md" data-testid="place-card-recommended-badge">
              <Star className="w-3 h-3 fill-white" /> Consigliato
            </div>
          )}
        </div>
      ) : (
        <div className="relative w-full h-40 flex items-center justify-center" style={{ background: tone.bg }}>
          <MapPin className="w-10 h-10" style={{ color: tone.icon, opacity: 0.55 }} />
          {place.host_recommended && (
            <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-amber-400 text-white text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shadow-md" data-testid="place-card-recommended-badge">
              <Star className="w-3 h-3 fill-white" /> Consigliato
            </div>
          )}
        </div>
      )}
      <div className="p-5">
        <p className="font-semibold text-zinc-900 text-[15px] tracking-tight truncate">{place.name}</p>
        <div className="flex items-center gap-2.5 mt-2 text-[11px] text-zinc-500 font-medium">
          {place.walking_time && <span className="flex items-center gap-1"><Navigation className="w-3 h-3" />{place.walking_time}</span>}
          {place.avg_price && <span className="flex items-center gap-0.5">{place.avg_price}</span>}
        </div>
        {place.why_go && <p className="text-[12px] text-zinc-500 mt-2 line-clamp-2 leading-relaxed">{place.why_go}</p>}
        {(place.discount_percent > 0) && (
          <span className="inline-block mt-3 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">-{place.discount_percent}%</span>
        )}
      </div>
    </motion.div>
  );
};

// ═══ PLACE DETAIL FULL PAGE (extracted to /components/PlaceDetailSheet.jsx) ═══
// The inline component was replaced with an OSM-map enabled full-screen sheet.


// ═══ EXTRA SERVICE CARD ═══
const ServiceCard = ({ service, index, t, extraContacts }) => {
  const wa = extraContacts?.whatsapp;
  const tg = extraContacts?.telegram;
  const ph = extraContacts?.phone;
  const hasGlobalContacts = wa || tg || ph;

  const waLink = wa ? (() => {
    const clean = wa.replace(/[^0-9]/g, "");
    const num = wa.startsWith("+") ? clean : `39${clean}`;
    return `https://wa.me/${num}?text=${encodeURIComponent(`Ciao! Vorrei richiedere: ${service.name || "servizio"}`)}`;
  })() : null;

  const tgLink = tg ? (tg.startsWith("@") ? `https://t.me/${tg.slice(1)}` : tg.startsWith("http") ? tg : `https://t.me/${tg.replace(/[^0-9]/g, "")}`) : null;

  return (
    <motion.div variants={cardIn} custom={index} className="bg-white rounded-3xl shadow-sm border border-zinc-100 overflow-hidden">
      <div className="p-5 flex items-center gap-4">
        <motion.div animate={iconPulse} className="w-12 h-12 bg-violet-50 rounded-2xl flex items-center justify-center shrink-0">
          {(() => { const IC = ICON_MAP[service.icon] || ShoppingBag; return <IC className="w-6 h-6 text-violet-600" />; })()}
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{service.name}</p>
          {service.description && <p className="text-xs text-zinc-400 mt-0.5">{service.description}</p>}
          {service.price && <p className="text-sm font-bold text-violet-600 mt-1">{service.price}</p>}
        </div>
      </div>
      {/* Contact buttons */}
      <div className="px-6 pb-4 flex gap-2 flex-wrap">
        {service.link && !service.link.startsWith("https://wa.me") && (
          <a href={service.link} target="_blank" rel="noopener noreferrer"
            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 bg-zinc-900 text-white rounded-xl py-2.5 font-bold text-xs">
            <ExternalLink className="w-3.5 h-3.5" /> {t.request}
          </a>
        )}
        {waLink && (
          <a href={waLink} target="_blank" rel="noopener noreferrer"
            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl py-2.5 font-bold text-xs">
            <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
          </a>
        )}
        {tgLink && (
          <a href={tgLink} target="_blank" rel="noopener noreferrer"
            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 bg-sky-500 text-white rounded-xl py-2.5 font-bold text-xs">
            <MessageCircle className="w-3.5 h-3.5" /> Telegram
          </a>
        )}
        {ph && (
          <a href={`tel:${ph}`}
            className="flex-1 min-w-[80px] flex items-center justify-center gap-1.5 bg-zinc-700 text-white rounded-xl py-2.5 font-bold text-xs">
            <Phone className="w-3.5 h-3.5" /> {t.call || "Chiama"}
          </a>
        )}
        {!service.link && !hasGlobalContacts && (
          <span className="text-xs text-zinc-300 font-medium py-2">{t.onRequest}</span>
        )}
      </div>
    </motion.div>
  );
};

// ═══ WASTE CARD ═══
const WasteCard = ({ waste, index }) => (
  <motion.div variants={cardIn} custom={index} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100 flex items-center gap-4">
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${waste.color || "#94A3B8"}15` }}>
      <Trash2 className="w-6 h-6" style={{ color: waste.color || "#94A3B8" }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-bold text-zinc-900 text-sm">{waste.type}</p>
      <p className="text-xs text-zinc-400 mt-0.5">{waste.days}</p>
      {waste.notes && <p className="text-xs text-zinc-500 mt-1">{waste.notes}</p>}
    </div>
  </motion.div>
);

// ═══ LANGUAGE SELECTOR ═══
const LangSelector = ({ lang, setLang }) => (
  <div
    className="flex w-full items-center justify-center gap-1.5 p-1 rounded-[22px] border border-white/50 bg-white/25 backdrop-blur-xl shadow-lg shadow-black/10"
    style={{ WebkitBackdropFilter: "blur(20px)", backdropFilter: "blur(20px)" }}
    data-testid="lang-selector"
  >
    {FLAGS.map(f => {
      const active = lang === f.code;
      return (
        <button
          key={f.code}
          onClick={() => setLang(f.code)}
          className="relative flex items-center justify-center gap-1.5 px-2.5 py-2 rounded-2xl outline-none"
          data-testid={`lang-${f.code}`}
          aria-label={f.code}
        >
          {active && (
            <motion.span
              layoutId="lang-active-pill"
              transition={{ type: "spring", stiffness: 360, damping: 28, mass: 0.8 }}
              className="absolute inset-0 rounded-[18px] overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.55) 55%, rgba(255,255,255,0.72) 100%)",
                backdropFilter: "blur(14px) saturate(180%)",
                WebkitBackdropFilter: "blur(14px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.8)",
                boxShadow: "0 8px 20px -8px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -2px 5px rgba(0,0,0,0.05)",
              }}
            />
          )}
          <motion.span
            animate={active ? { scale: [1, 0.8, 1.18, 1] } : { scale: 1 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className={`relative z-10 text-[20px] leading-none transition-all duration-200 ${active ? "brightness-110" : "opacity-60 grayscale-[30%]"}`}
          >
            {f.flag}
          </motion.span>
          <span className={`relative z-10 text-[11px] font-black tracking-wide leading-none transition-colors duration-200 ${active ? "text-zinc-900" : "text-zinc-500"}`}>
            {f.abbr}
          </span>
        </button>
      );
    })}
  </div>
);

// ═══ GETYOURGUIDE CAROUSEL ═══
const GYG_PARTNER_ID = "TDY7OAA";
const GYG_LOCALE_MAP = { it: "it-IT", en: "en-US", fr: "fr-FR", es: "es-ES", de: "de-DE" };

const GYGCarousel = ({ location, lang }) => {
  const containerRef = useRef(null);
  const TOTAL_ITEMS = 30;

  useEffect(() => {
    if (!location || !containerRef.current) return;
    const el = containerRef.current;
    el.innerHTML = "";

    const widget = document.createElement("div");
    widget.setAttribute("data-gyg-href", "https://widget.getyourguide.com/default/activities.frame");
    widget.setAttribute("data-gyg-locale-code", GYG_LOCALE_MAP[lang] || "en-US");
    widget.setAttribute("data-gyg-widget", "activities");
    widget.setAttribute("data-gyg-number-of-items", String(TOTAL_ITEMS));
    widget.setAttribute("data-gyg-partner-id", GYG_PARTNER_ID);
    widget.setAttribute("data-gyg-q", location);
    el.appendChild(widget);

    if (!document.querySelector('script[src*="getyourguide.com"]')) {
      const script = document.createElement("script");
      script.src = "https://widget.getyourguide.com/dist/pa.umd.production.min.js";
      script.async = true;
      script.setAttribute("data-gyg-partner-id", GYG_PARTNER_ID);
      document.head.appendChild(script);
    }

    return () => { el.innerHTML = ""; };
  }, [location, lang]);

  const widgetCSS = `
    .gyg-widget-container iframe { border-radius: 16px !important; border: none !important; min-height: 720px; width: 100% !important; }
    .gyg-widget-container > div { border-radius: 16px !important; overflow: hidden !important; }
  `;

  // i18n for the "see more" CTA (4 languages supported by GuestbookApp)
  const moreLabel = { it: "Vedi tutte le esperienze su GetYourGuide", en: "See all experiences on GetYourGuide", fr: "Voir toutes les expériences sur GetYourGuide", es: "Ver todas las experiencias en GetYourGuide", de: "Alle Erlebnisse auf GetYourGuide ansehen" }[lang] || "See all experiences on GetYourGuide";
  const gygFullUrl = `https://www.getyourguide.com/s/?q=${encodeURIComponent(location)}&partner_id=${GYG_PARTNER_ID}`;

  return (
    <motion.div variants={cardIn} custom={5} initial="hidden" animate="visible" className="mb-8" data-testid="gyg-carousel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Experience</p>
          <span className="text-[9px] font-bold bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">GetYourGuide</span>
        </div>
        <p className="text-[10px] text-zinc-400 font-medium">{location}</p>
      </div>
      <div ref={containerRef} className="gyg-widget-container rounded-2xl overflow-hidden" />

      <a
        href={gygFullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 flex items-center justify-center gap-2 w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-bold py-3 rounded-2xl hover:shadow-lg transition-shadow"
        data-testid="gyg-see-more"
      >
        {moreLabel}
        <ChevronRight className="w-4 h-4" />
      </a>

      <style>{widgetCSS}</style>
    </motion.div>
  );
};


// ═══ BOTTOM NAV WITH SWIPE + ARROW HINT ═══
const BottomNav = ({ tabs, tab, setTab, promo }) => {
  // Tab renderizzata come slot a larghezza uguale (flex-1) così due gruppi
  // bilanciati ai lati del FAB restano SEMPRE simmetrici: il gap centrale e
  // il FAB (left-1/2 della barra) condividono lo stesso centro a qualsiasi
  // larghezza, senza scroll orizzontale e senza spostamenti tra i tab.
  const renderTab = ({ id, icon: Icon, label }) => {
    const isActive = tab === id;
    return (
      <button key={id} onClick={() => setTab(id)}
        className="relative flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-2xl flex-1 min-w-0"
        data-testid={`nav-${id}`}>
        {isActive && (
          <motion.span
            layoutId="nav-glass-bubble"
            className="nav-glass-bubble"
            transition={{ type: "spring", stiffness: 480, damping: 36 }}
          />
        )}
        <Icon className={`relative z-10 w-5 h-5 shrink-0 transition-colors ${isActive ? "text-zinc-900" : "text-zinc-400"}`} />
        <span className={`relative z-10 text-[9px] font-bold transition-colors truncate max-w-full ${isActive ? "text-zinc-900" : "text-zinc-500"}`}>{label}</span>
      </button>
    );
  };

  const mid = Math.ceil(tabs.length / 2);
  const leftTabs = tabs.slice(0, mid);
  const rightTabs = tabs.slice(mid);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30 bg-white/80 border-t border-zinc-100" style={{ backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}>
      {promo?.show && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-0 z-40 py-2 pb-[max(0.6rem,env(safe-area-inset-bottom))]">
          <button
            onClick={() => { vibrate([12, 45, 12]); promo.onClick && promo.onClick(); }}
            data-testid="nav-promos-fab"
            aria-label={promo.label}
            className="flex flex-col items-center gap-0.5 px-2 py-1.5"
          >
            <span className="relative h-5 w-16 flex items-center justify-center">
              {/* collare: linea sottile che riprende il bordo del footer e avvolge il tondo che sporge, con un paio di px di margine */}
              <span
                className="absolute left-1/2 -translate-x-1/2 bottom-[-8px] w-[66px] h-[66px] rounded-full z-0 border border-zinc-200/70"
                style={{ background: "rgba(255,255,255,0.8)", backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)" }}
              />
              <motion.span
                whileTap={{ scale: 0.92 }}
                className="absolute left-1/2 -ml-8 bottom-[-7px] w-16 h-16 rounded-full flex items-center justify-center z-10"
                style={{ background: "linear-gradient(135deg,#FF6B60 0%,#FF3B30 60%,#D42A20 100%)", boxShadow: "0 12px 30px -6px rgba(255,59,48,0.85)" }}
              >
                <BadgePercent className="w-8 h-8 text-white" />
              </motion.span>
            </span>
            <span className="text-[13px] font-bold whitespace-nowrap" style={{ marginTop: "7px", color: promo.active ? "#D42A20" : "#FF3B30" }}>{promo.label}</span>
          </button>
        </div>
      )}
      <div className="flex items-stretch py-2 pb-[max(0.6rem,env(safe-area-inset-bottom))] px-1.5">
        {promo?.show ? (
          <>
            <div className="flex-1 flex items-stretch min-w-0">{leftTabs.map(renderTab)}</div>
            <div className="w-16 shrink-0" aria-hidden />
            <div className="flex-1 flex items-stretch min-w-0">{rightTabs.map(renderTab)}</div>
          </>
        ) : (
          <div className="flex-1 flex items-stretch min-w-0">{tabs.map(renderTab)}</div>
        )}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════
// MAIN GUESTBOOK APP
// ═══════════════════════════════════════
const SectionLabel = ({ children, color = "#0EA5E9" }) => (
  <div className="flex items-center gap-2 mb-2">
    <span className="w-1 h-4 rounded-full shrink-0" style={{ background: color }} />
    <p className="text-[15px] font-extrabold tracking-tight text-zinc-900">{children}</p>
  </div>
);

// ═══ MANUAL ITEM (Home) ═══
// Aligned to the check-in section typography: same body text size, colour and
// leading. The whole "Manuali" card is now tinted rose so it stands out from
// the sky-tinted Check-in and Wi-Fi cards without breaking the visual system.
const HomeManualItem = ({ m, lang, index }) => {
  const [open, setOpen] = useState(false);
  const isLong = (m.content || "").length > 120 || (m.content || "").split("\n").length > 3;
  return (
    <motion.button
      type="button"
      onClick={() => isLong && setOpen(!open)}
      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.06 * index }}
      className="w-full text-left bg-white/70 hover:bg-white rounded-2xl p-4 border border-rose-100 transition-colors"
      data-testid={`home-manual-item-${index}`}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{m.title}</p>
        {isLong && <ChevronDown className={`w-4 h-4 text-zinc-300 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />}
      </div>
      {m.content && (
        <p className={`text-[14px] text-zinc-700 mt-1.5 whitespace-pre-line leading-relaxed ${open ? "" : "line-clamp-3"}`}>{m.content}</p>
      )}
      {isLong && !open && (
        <p className="text-[11px] font-bold text-rose-500 mt-1.5">{lang === "it" ? "Leggi tutto →" : lang === "fr" ? "Lire tout →" : lang === "es" ? "Leer todo →" : lang === "de" ? "Alles lesen →" : "Read all →"}</p>
      )}
    </motion.button>
  );
};

// ═══ AI TOUR ═══
// Prompt del giorno + itinerario generato via Gemini Flash.
// Regola tassativa: 1 tour al giorno per property (enforced lato server).
// ═══ TRANSPORTS — public renderer for the "Trasporti locali" page ═══
// One card per active category. Same accent colour as the admin. Groups
// are used to model tabs (car/bike sharing) and sub-mezzi (airport).
const TransportItemCard = ({ item, color, lang }) => {
  const hasWebsite = !!item.website;
  const hasPhone = !!item.phone;
  const hasApple = !!item.apple_app;
  const hasGoogle = !!item.google_play;
  const hasApps = hasApple || hasGoogle;
  const hasPrimary = hasWebsite || hasPhone;
  // Brand marks — inline monochrome SVGs. We render both apps on the same
  // black surface so the row reads as a single "Official Apps" strip; this
  // is the pattern users are trained to recognise from app landing pages.
  const AppleMark = ({ size = 14 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.04c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.47.75-3.11.75-.65 0-1.63-.73-2.68-.71-1.38.02-2.65.8-3.36 2.03-1.44 2.49-.37 6.18 1.03 8.2.68.99 1.49 2.1 2.55 2.06 1.02-.04 1.41-.66 2.65-.66 1.24 0 1.58.66 2.66.64 1.1-.02 1.79-1.01 2.46-2 .77-1.15 1.09-2.26 1.11-2.32-.02-.01-2.13-.82-2.15-3.24zm-2.05-5.96c.56-.69.94-1.63.84-2.58-.81.03-1.8.54-2.38 1.22-.52.6-.98 1.58-.85 2.5.9.07 1.83-.45 2.39-1.14z" />
    </svg>
  );
  // Google Play "triangle" mark (monochrome). The classic 4-color triangle
  // is a registered trademark — using a solid monochrome version keeps the
  // look official without misusing the brand asset.
  const GooglePlayMark = ({ size = 14 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M4.5 3.2c-.3.2-.5.6-.5 1.1v15.4c0 .5.2.9.5 1.1l8.7-8.8L4.5 3.2z" />
      <path d="M16 8.8L5.6 2.9c-.2-.1-.5-.2-.7-.1L14.1 12l1.9-3.2z" />
      <path d="M19.4 10.9l-3.4-2-2.1 2.1 2.1 2.1 3.4-2c1.1-.6 1.1-1.6 0-2.2z" />
      <path d="M5 21.2c.2.1.5 0 .7-.1L16 15.2l-1.9-3.2-9.1 9.2z" />
    </svg>
  );

  const CTA_APPS = {
    it: "Scarica le App Dedicate", en: "Download the Apps", fr: "Télécharger les Apps", es: "Descarga las Apps", de: "Apps herunterladen",
  };

  return (
    <div className="rounded-2xl bg-white border border-zinc-100 p-4 shadow-sm" data-testid={`transport-public-item-${item.id || item.name}`}>
      <p className="text-[15px] font-black text-zinc-900 tracking-tight leading-tight">{item.name}</p>
      {item.description && (
        <p className="text-[13px] text-zinc-600 leading-relaxed mt-1.5 whitespace-pre-line">{item.description}</p>
      )}

      {/* Primary actions: Chiama + Sito web */}
      {hasPrimary && (
        <div className="flex flex-wrap gap-2 mt-3">
          {hasPhone && (
            <a href={`tel:${item.phone}`} className="inline-flex items-center gap-1.5 text-white text-[11px] font-black px-3 py-2 rounded-xl shadow-sm active:opacity-90 transition-opacity" style={{ background: color }} data-testid={`transport-call-${item.id || item.name}`}>
              <Phone className="w-3 h-3" strokeWidth={2.4} />
              {TRANSPORT_ACTIONS.call[lang] || TRANSPORT_ACTIONS.call.it}
            </a>
          )}
          {hasWebsite && (
            <a href={item.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-[11px] font-black px-3 py-2 rounded-xl shadow-sm hover:bg-zinc-800 transition-colors">
              <ExternalLink className="w-3 h-3" strokeWidth={2.4} />
              {TRANSPORT_ACTIONS.website[lang] || TRANSPORT_ACTIONS.website.it}
            </a>
          )}
        </div>
      )}

      {/* Apps section — divider + official monochrome badges */}
      {hasApps && (
        <>
          <div className="flex items-center gap-2 mt-4 mb-2.5" aria-hidden="true">
            <span className="flex-1 h-px bg-zinc-200" />
            <span className="text-[9.5px] font-black uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">
              {CTA_APPS[lang] || CTA_APPS.it}
            </span>
            <span className="flex-1 h-px bg-zinc-200" />
          </div>
          <div className={`grid gap-2.5 ${hasApple && hasGoogle ? "grid-cols-2" : "grid-cols-1"}`}>
            {hasApple && (
              <a
                href={item.apple_app}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-zinc-900 text-white rounded-2xl px-2.5 py-2.5 shadow-md hover:bg-black transition-colors active:scale-[0.98] transform"
                data-testid={`transport-appstore-${item.id || item.name}`}
              >
                <AppleMark size={24} />
                <div className="min-w-0 text-left flex flex-col">
                  <span className="text-[8.5px] font-medium text-white/80 tracking-wide leading-none mb-1 whitespace-nowrap">
                    {lang === "it" ? "Scarica su" : lang === "fr" ? "Télécharger sur" : lang === "es" ? "Descarga en" : lang === "de" ? "Laden im" : "Download on the"}
                  </span>
                  <span className="text-[14px] font-black tracking-tight leading-none whitespace-nowrap">App Store</span>
                </div>
              </a>
            )}
            {hasGoogle && (
              <a
                href={item.google_play}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white rounded-2xl px-2.5 py-2.5 shadow-md transition-opacity active:scale-[0.98] hover:opacity-95 transform"
                style={{ background: "#3DDC84" }}
                data-testid={`transport-play-${item.id || item.name}`}
              >
                <GooglePlayMark size={24} />
                <div className="min-w-0 text-left flex flex-col">
                  <span className="text-[8.5px] font-medium text-white/95 tracking-wide leading-none mb-1 whitespace-nowrap">
                    {lang === "it" ? "Disponibile su" : lang === "fr" ? "Disponible sur" : lang === "es" ? "Disponible en" : lang === "de" ? "Jetzt bei" : "Get it on"}
                  </span>
                  <span className="text-[14px] font-black tracking-tight leading-none whitespace-nowrap">Google Play</span>
                </div>
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const TransportCategoryCard = ({ cat, groups, lang }) => {
  const [activeTab, setActiveTab] = useState(0);
  const Icon = cat.icon;
  const showTabs = groups.length > 1;
  const g = groups[activeTab] || groups[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-3xl overflow-hidden bg-white border border-zinc-100 shadow-sm"
      data-testid={`transport-card-${cat.id}`}
    >
      <div className="px-4 py-3 flex items-center gap-3" style={{ background: `${cat.color}12` }}>
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${cat.color}22` }}>
          <Icon className="w-5 h-5" style={{ color: cat.color }} strokeWidth={1.9} />
        </div>
        <div className="min-w-0">
          <p className="text-[14px] font-black text-zinc-900 tracking-tight leading-tight truncate">{cat.labels[lang] || cat.labels.it}</p>
          {showTabs && (
            <p className="text-[11px] font-semibold leading-tight truncate" style={{ color: `${cat.color}CC` }}>
              {groups.length} {lang === "it" ? "sezioni" : lang === "fr" ? "sections" : lang === "es" ? "secciones" : lang === "de" ? "Bereiche" : "sections"}
            </p>
          )}
        </div>
      </div>
      {showTabs && (
        <div className="px-4 pt-3 flex items-center gap-2 flex-wrap">
          {groups.map((gr, i) => {
            const GIcon = gr.icon;
            return (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black transition-all ${activeTab === i ? "text-white" : "text-zinc-600 bg-zinc-100 hover:bg-zinc-200"}`}
                style={activeTab === i ? { background: cat.color } : {}}
                data-testid={`transport-tab-${cat.id}-${i}`}
              >
                {GIcon && <GIcon className="w-3 h-3" strokeWidth={2.2} />}
                {gr.title || `Tab ${i + 1}`}
              </button>
            );
          })}
        </div>
      )}
      <div className="p-4 space-y-2.5">
        {(g.items || []).map((it, i) => (
          <TransportItemCard key={it.id || i} item={it} color={cat.color} lang={lang} />
        ))}
      </div>
    </motion.div>
  );
};

// Parse lat,lng from common Google Maps URL formats → [lat, lng] or null
const parseLatLng = (url) => {
  if (!url || typeof url !== "string") return null;
  let m = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  m = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  m = url.match(/[?&](?:q|query|ll|center|destination)=(-?\d+\.\d+),\s*(-?\d+\.\d+)/);
  if (m) return [parseFloat(m[1]), parseFloat(m[2])];
  return null;
};

// Build an OpenStreetMap raster-tile URL for the tile containing lat/lng.
// (staticmap composites are unreliable/blocked; single tiles from
// tile.openstreetmap.org are the only no-key option that loads.)
const osmTileCover = (lat, lng, z = 16) => {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const r = (lat * Math.PI) / 180;
  const y = Math.floor(((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * n);
  return `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
};

// ═══ LUGGAGE STRUCTURE CARD — public renderer for "Deposito Bagagli" ═══
// Mirrors the transports item card: name, description, distance badge,
// directions + website buttons, optional Apple/Google app badges and an
// optional "convenzione" (special deal) banner.
const LuggageStructureCard = ({ item, lang, index }) => {
  const L = LUGGAGE_LABELS;
  const dist = fmtDistance(item.distance_m);
  const hasMap = !!item.map_url;
  const hasWebsite = !!item.website;
  const hasApple = !!item.apple_app;
  const hasGoogle = !!item.google_play;
  const hasApps = hasApple || hasGoogle;
  const color = LUGGAGE_ACCENT.color;

  // Cover image: real OSM map tile when we can parse coordinates from the Maps
  // link, otherwise the generic luggage photo. Only shown when a map link is set.
  const coords = parseLatLng(item.map_url);
  const isMapTile = !!coords;
  const cover = hasMap
    ? (coords ? osmTileCover(coords[0], coords[1]) : "/luggage-storage.jpg")
    : null;

  const AppleMark = ({ size = 22 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.04c-.02-2.05 1.68-3.03 1.75-3.08-.95-1.4-2.44-1.59-2.97-1.61-1.26-.13-2.47.75-3.11.75-.65 0-1.63-.73-2.68-.71-1.38.02-2.65.8-3.36 2.03-1.44 2.49-.37 6.18 1.03 8.2.68.99 1.49 2.1 2.55 2.06 1.02-.04 1.41-.66 2.65-.66 1.24 0 1.58.66 2.66.64 1.1-.02 1.79-1.01 2.46-2 .77-1.15 1.09-2.26 1.11-2.32-.02-.01-2.13-.82-2.15-3.24zm-2.05-5.96c.56-.69.94-1.63.84-2.58-.81.03-1.8.54-2.38 1.22-.52.6-.98 1.58-.85 2.5.9.07 1.83-.45 2.39-1.14z" />
    </svg>
  );
  const GooglePlayMark = ({ size = 22 }) => (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M4.5 3.2c-.3.2-.5.6-.5 1.1v15.4c0 .5.2.9.5 1.1l8.7-8.8L4.5 3.2z" />
      <path d="M16 8.8L5.6 2.9c-.2-.1-.5-.2-.7-.1L14.1 12l1.9-3.2z" />
      <path d="M19.4 10.9l-3.4-2-2.1 2.1 2.1 2.1 3.4-2c1.1-.6 1.1-1.6 0-2.2z" />
      <path d="M5 21.2c.2.1.5 0 .7-.1L16 15.2l-1.9-3.2-9.1 9.2z" />
    </svg>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 * (index || 0) }}
      className="rounded-2xl bg-white border border-zinc-100 p-4 shadow-sm"
      data-testid={`luggage-public-item-${item.id || item.name}`}
    >
      {cover && (
        <div className="relative -mx-4 -mt-4 mb-3 h-28 overflow-hidden rounded-t-2xl bg-zinc-100">
          <img
            src={cover}
            alt={item.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => { if (e.currentTarget.src.indexOf("luggage-storage.jpg") === -1) e.currentTarget.src = "/luggage-storage.jpg"; }}
          />
          {isMapTile && (
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-full drop-shadow-md">
              <MapPin className="w-7 h-7" style={{ color }} strokeWidth={2.4} fill="#fff" />
            </span>
          )}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg,rgba(0,0,0,0) 55%,rgba(0,0,0,0.28) 100%)" }} />
          {dist && (
            <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full text-white shadow-sm" style={{ background: color }}>
              <MapPin className="w-3 h-3" strokeWidth={2.4} /> {dist}
            </span>
          )}
        </div>
      )}
      <div className="flex items-start justify-between gap-2">
        <p className="text-[15px] font-black text-zinc-900 tracking-tight leading-tight">{item.name}</p>
        {dist && !cover && (
          <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-black px-2.5 py-1 rounded-full" style={{ background: `${color}18`, color }}>
            <MapPin className="w-3 h-3" strokeWidth={2.4} /> {dist}
          </span>
        )}
      </div>

      {item.description && (
        <p className="text-[13px] text-zinc-600 leading-relaxed mt-1.5 whitespace-pre-line">{item.description}</p>
      )}

      {item.convention && (
        <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
          <Gift className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={2.2} />
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-wider text-amber-700 leading-none">{L.convention[lang] || L.convention.it}</p>
            <p className="text-[12px] text-amber-800 font-semibold leading-snug mt-1">{item.convention}</p>
          </div>
        </div>
      )}

      {(hasMap || hasWebsite) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {hasMap && (
            <a href={item.map_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-white text-[11px] font-black px-3 py-2 rounded-xl shadow-sm active:opacity-90 transition-opacity" style={{ background: color }} data-testid={`luggage-directions-${item.id || item.name}`}>
              <Navigation className="w-3 h-3" strokeWidth={2.4} /> {L.directions[lang] || L.directions.it}
            </a>
          )}
          {hasWebsite && (
            <a href={item.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 bg-zinc-900 text-white text-[11px] font-black px-3 py-2 rounded-xl shadow-sm hover:bg-zinc-800 transition-colors">
              <ExternalLink className="w-3 h-3" strokeWidth={2.4} /> {L.website[lang] || L.website.it}
            </a>
          )}
        </div>
      )}

      {hasApps && (
        <>
          <div className="flex items-center gap-2 mt-4 mb-2.5" aria-hidden="true">
            <span className="flex-1 h-px bg-zinc-200" />
            <span className="text-[9.5px] font-black uppercase tracking-[0.14em] text-zinc-400 whitespace-nowrap">{L.apps[lang] || L.apps.it}</span>
            <span className="flex-1 h-px bg-zinc-200" />
          </div>
          <div className={`grid gap-2.5 ${hasApple && hasGoogle ? "grid-cols-2" : "grid-cols-1"}`}>
            {hasApple && (
              <a href={item.apple_app} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-zinc-900 text-white rounded-2xl px-2.5 py-2.5 shadow-md hover:bg-black transition-colors active:scale-[0.98] transform" data-testid={`luggage-appstore-${item.id || item.name}`}>
                <AppleMark size={24} />
                <div className="min-w-0 text-left flex flex-col">
                  <span className="text-[8.5px] font-medium text-white/80 tracking-wide leading-none mb-1 whitespace-nowrap">{lang === "it" ? "Scarica su" : lang === "fr" ? "Télécharger sur" : lang === "es" ? "Descarga en" : lang === "de" ? "Laden im" : "Download on the"}</span>
                  <span className="text-[14px] font-black tracking-tight leading-none whitespace-nowrap">App Store</span>
                </div>
              </a>
            )}
            {hasGoogle && (
              <a href={item.google_play} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white rounded-2xl px-2.5 py-2.5 shadow-md transition-opacity active:scale-[0.98] hover:opacity-95 transform" style={{ background: "#3DDC84" }} data-testid={`luggage-play-${item.id || item.name}`}>
                <GooglePlayMark size={24} />
                <div className="min-w-0 text-left flex flex-col">
                  <span className="text-[8.5px] font-medium text-white/95 tracking-wide leading-none mb-1 whitespace-nowrap">{lang === "it" ? "Disponibile su" : lang === "fr" ? "Disponible sur" : lang === "es" ? "Disponible en" : lang === "de" ? "Jetzt bei" : "Get it on"}</span>
                  <span className="text-[14px] font-black tracking-tight leading-none whitespace-nowrap">Google Play</span>
                </div>
              </a>
            )}
          </div>
        </>
      )}
    </motion.div>
  );
};

const AI_TOUR_LABELS = {
  it: { title: "Tour AI della città", sub: "Un itinerario personalizzato in {city}", pick: "Quanto tempo hai?", h12: "12 ore", h24: "24 ore", h36: "36 ore", generate: "Genera il mio Tour AI", generating: "Sto creando il tuo itinerario…", already: "Hai già generato il tuo tour di oggi", tourFor: "Tour di", share: "Condividi su WhatsApp", regenerate_note: "Il tuo Tour AI personale del soggiorno ✨", footer: "Questo tour è stato fornito e costruito da Hostland — hostland.vitadahost.it", steps: ["Analizzo la tua posizione", "Cerco i posti più amati dai locali", "Filtro solo le recensioni migliori (4.4+★)", "Verifico orari di apertura", "Costruisco l'itinerario ottimale", "Aggiungo consigli e dritte segrete", "Ultimi ritocchi in corso"] },
  en: { title: "AI City Tour", sub: "A personalised itinerary in {city}", pick: "How much time?", h12: "12 hours", h24: "24 hours", h36: "36 hours", generate: "Generate my AI Tour", generating: "Crafting your itinerary…", already: "You've already generated today's tour", tourFor: "Tour of", share: "Share on WhatsApp", regenerate_note: "Your personal AI Tour for the stay ✨", footer: "This tour was crafted by Hostland — hostland.vitadahost.it", steps: ["Analysing your location", "Finding places locals actually love", "Filtering by top reviews (4.4+★)", "Checking opening hours", "Building the optimal itinerary", "Adding insider tips", "Final touches"] },
  fr: { title: "Tour IA de la ville", sub: "Un itinéraire personnalisé à {city}", pick: "Combien de temps ?", h12: "12 heures", h24: "24 heures", h36: "36 heures", generate: "Générer mon Tour IA", generating: "Création de votre itinéraire…", already: "Vous avez déjà généré le tour d'aujourd'hui", tourFor: "Tour de", share: "Partager sur WhatsApp", regenerate_note: "Votre Tour IA personnel du séjour ✨", footer: "Ce tour a été créé par Hostland — hostland.vitadahost.it", steps: ["J'analyse votre position", "Je cherche les lieux préférés des locaux", "Je filtre les meilleures notes (4.4+★)", "Je vérifie les horaires d'ouverture", "Je construis l'itinéraire idéal", "J'ajoute les conseils d'initié", "Dernières retouches"] },
  es: { title: "Tour IA de la ciudad", sub: "Un itinerario personalizado en {city}", pick: "¿Cuánto tiempo tienes?", h12: "12 horas", h24: "24 horas", h36: "36 horas", generate: "Generar mi Tour IA", generating: "Creando tu itinerario…", already: "Ya has generado tu tour de hoy", tourFor: "Tour de", share: "Compartir en WhatsApp", regenerate_note: "Tu Tour IA personal de la estancia ✨", footer: "Este tour ha sido creado por Hostland — hostland.vitadahost.it", steps: ["Analizando tu ubicación", "Buscando los sitios que aman los locales", "Filtrando por mejores reseñas (4.4+★)", "Verificando horarios", "Construyendo el itinerario óptimo", "Añadiendo consejos secretos", "Últimos retoques"] },
  de: { title: "KI-Stadttour", sub: "Eine personalisierte Route in {city}", pick: "Wie viel Zeit hast du?", h12: "12 Stunden", h24: "24 Stunden", h36: "36 Stunden", generate: "Meine KI-Tour erstellen", generating: "Erstelle deine Route…", already: "Du hast heute schon eine Tour erstellt", tourFor: "Tour durch", share: "Auf WhatsApp teilen", regenerate_note: "Dein persönlicher KI-Tour für den Aufenthalt ✨", footer: "Diese Tour wurde von Hostland erstellt — hostland.vitadahost.it", steps: ["Analysiere deinen Standort", "Suche Orte, die Einheimische lieben", "Filtere Top-Bewertungen (4.4+★)", "Prüfe Öffnungszeiten", "Erstelle die optimale Route", "Füge Insider-Tipps hinzu", "Letzte Feinschliffe"] },
};

const CATEGORY_ICON = {
  museum: "🏛️", monument: "🏛️", park: "🌳", viewpoint: "📸",
  food: "🍽️", coffee: "☕", street: "🚶", shop: "🛍️", hidden_gem: "💎",
};

const AiTourLoader = ({ L }) => {
  const [progress, setProgress] = useState(6);
  const [stepIdx, setStepIdx] = useState(0);
  const steps = L.steps || [];

  useEffect(() => {
    // Progress bar animation: asintotic to 92% so the user never sees "100%"
    // before the real response lands. Interval keeps it smooth and "alive".
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - start) / 1000; // seconds
      // 1 - e^(-t/tau) where tau=4s → hits ~85% at 8s, ~92% at 10s
      const eased = 1 - Math.exp(-elapsed / 4);
      setProgress(6 + Math.min(86, Math.round(eased * 86)));
    }, 120);

    // Cycle the step labels every ~1.6s so it feels like real progress
    const stepTick = setInterval(() => {
      setStepIdx((i) => (i + 1 < steps.length ? i + 1 : i));
    }, 1600);

    return () => { clearInterval(tick); clearInterval(stepTick); };
  }, [steps.length]);

  return (
    <div className="space-y-4" data-testid="ai-tour-loader">
      {/* AI header — icona pulsante + label "TOUR AI · GEMINI" */}
      <div className="flex items-center gap-2.5">
        <motion.div
          animate={{ scale: [1, 1.15, 1], rotate: [0, 8, -8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-10 h-10 bg-fuchsia-500 rounded-2xl flex items-center justify-center shadow-lg shadow-fuchsia-500/30"
        >
          <Wand2 className="w-5 h-5 text-white" />
        </motion.div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600">Tour AI · Gemini</span>
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-fuchsia-400 opacity-80" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-fuchsia-500" />
            </span>
          </div>
          <p className="text-[13px] font-bold text-zinc-900 truncate">{steps[stepIdx] || L.generating}</p>
        </div>
        <span className="text-[13px] font-black tabular-nums text-fuchsia-600 shrink-0">{progress}%</span>
      </div>

      {/* Progress bar — gradient stripe with sheen */}
      <div className="relative h-2.5 bg-zinc-100 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ background: "linear-gradient(90deg, #A855F7 0%, #D946EF 50%, #EC4899 100%)" }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
        {/* Moving sheen overlay */}
        <motion.div
          className="absolute inset-y-0 w-1/3 opacity-60"
          style={{ background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.85) 50%, transparent 100%)" }}
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
        />
      </div>

      {/* Step list — checkmark done → spinner current → dot pending */}
      <div className="space-y-1.5 pt-1">
        {steps.map((s, i) => {
          const done = i < stepIdx;
          const current = i === stepIdx;
          return (
            <div key={i} className="flex items-center gap-2.5 text-[12px] leading-tight">
              {done ? (
                <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </div>
              ) : current ? (
                <div className="w-4 h-4 shrink-0"><Loader className="w-4 h-4 text-fuchsia-500 animate-spin" /></div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-zinc-200 shrink-0" />
              )}
              <span className={done ? "text-zinc-400 line-through" : current ? "text-zinc-900 font-bold" : "text-zinc-400"}>{s}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AiTourCard = ({ propertyId, city, lang }) => {
  const L = AI_TOUR_LABELS[lang] || AI_TOUR_LABELS.it;
  const [duration, setDuration] = useState(24);
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkedToday, setCheckedToday] = useState(false);

  // Rehydrate: un solo Tour AI per dispositivo/soggiorno (cache locale), poi fallback server
  useEffect(() => {
    let cancelled = false;
    try {
      const cached = localStorage.getItem(`gb_ai_tour_${propertyId}`);
      if (cached) {
        setTour(JSON.parse(cached));
        setCheckedToday(true);
        return;
      }
    } catch { /* ignore */ }
    axios.get(`${API}/guestbook/${propertyId}/ai-tour`).then(r => {
      if (cancelled) return;
      if (r.data && r.data.tour) {
        setTour(r.data);
        try { localStorage.setItem(`gb_ai_tour_${propertyId}`, JSON.stringify(r.data)); } catch { /* ignore */ }
      }
      setCheckedToday(true);
    }).catch(() => setCheckedToday(true));
    return () => { cancelled = true; };
  }, [propertyId]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/guestbook/${propertyId}/ai-tour`,
        { duration_hours: duration, language: lang });
      setTour(r.data.tour);
      try { localStorage.setItem(`gb_ai_tour_${propertyId}`, JSON.stringify(r.data.tour)); } catch { /* ignore */ }
      if (r.data.already_generated) toast.info(L.already);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore, riprova");
    } finally {
      setLoading(false);
    }
  };

  const shareWhatsapp = () => {
    if (!tour?.tour) return;
    const t = tour.tour;
    const lines = [
      `🌍 ${t.title}`,
      t.summary,
      "",
      ...t.stops.map(s => `${s.time} — ${CATEGORY_ICON[s.category] || "📍"} ${s.name}\n${s.description}${s.tip ? `\n💡 ${s.tip}` : ""}`),
      "",
      L.footer,
    ];
    const text = encodeURIComponent(lines.join("\n\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  return (
    <motion.div variants={cardIn} custom={4} initial="hidden" animate="visible" className="mt-4">
      <div className="bg-white rounded-3xl shadow-sm border border-zinc-100 overflow-hidden">
        {/* Header — coerente col resto (fuchsia/purple accent per distinguerlo) */}
        <div className="bg-fuchsia-50 px-5 py-3.5 flex items-center gap-3 border-b border-fuchsia-100">
          <div className="w-10 h-10 bg-fuchsia-500 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-extrabold text-zinc-900 tracking-tight">{L.title}</p>
            <p className="text-[11px] text-fuchsia-600 font-bold truncate">{L.sub.replace("{city}", city)}</p>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {!tour && !loading && (
            <>
              <SectionLabel color="#D946EF">{L.pick}</SectionLabel>
              <div className="grid grid-cols-3 gap-2" data-testid="ai-tour-duration-picker">
                {[12, 24, 36].map(h => (
                  <button key={h} type="button" onClick={() => setDuration(h)}
                    className={`py-2.5 rounded-xl border-2 text-sm font-bold transition-all ${duration === h ? "border-fuchsia-500 bg-fuchsia-500 text-white" : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-300"}`}
                    data-testid={`ai-tour-h${h}-btn`}>
                    {L[`h${h}`]}
                  </button>
                ))}
              </div>
              <motion.button whileTap={{ scale: 0.96 }} disabled={!checkedToday} onClick={generate}
                className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 bg-zinc-900 text-white disabled:opacity-60"
                data-testid="ai-tour-generate-btn">
                <Sparkles className="w-4 h-4" /> {L.generate}
              </motion.button>
            </>
          )}

          {loading && <AiTourLoader L={L} />}

          {tour?.tour && (
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-fuchsia-600 mb-1">{L.tourFor} {tour.city} · {tour.duration_hours}h</p>
                <h3 className="text-xl font-black text-zinc-900 tracking-tight leading-tight">{tour.tour.title}</h3>
                <p className="text-[14px] text-zinc-600 mt-1.5 leading-relaxed">{tour.tour.summary}</p>
              </div>

              {/* Timeline */}
              <div className="space-y-3">
                {(tour.tour.stops || []).map((s, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center shrink-0">
                      <div className="w-9 h-9 rounded-full bg-fuchsia-50 border-2 border-fuchsia-200 flex items-center justify-center text-base">
                        {CATEGORY_ICON[s.category] || "📍"}
                      </div>
                      {i < (tour.tour.stops.length - 1) && <div className="flex-1 w-0.5 bg-fuchsia-100 my-1 min-h-[16px]" />}
                    </div>
                    <div className="flex-1 min-w-0 pb-2">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-[11px] font-black text-fuchsia-600 tracking-wider">{s.time}</span>
                        <span className="text-[10px] text-zinc-400 font-semibold">{s.duration_min}′</span>
                      </div>
                      <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{s.name}</p>
                      {s.address_hint && <p className="text-[11px] text-zinc-400">{s.address_hint}</p>}
                      <p className="text-[14px] text-zinc-700 mt-1 leading-relaxed">{s.description}</p>
                      {s.tip && (
                        <div className="mt-1.5 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                          <p className="text-[12px] text-amber-800"><strong>💡</strong> {s.tip}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Share */}
              <button type="button" onClick={shareWhatsapp}
                className="w-full py-3 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 bg-emerald-500 text-white"
                data-testid="ai-tour-share-btn">
                <MessageCircle className="w-4 h-4" /> {L.share}
              </button>

              <p className="text-center text-[10px] text-zinc-400 font-medium leading-relaxed pt-1">
                {L.footer}<br/>{L.regenerate_note}
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Desktop-only phone frame for the PUBLIC DEMO Welcome Book so it always looks
// smartphone-optimised (iPhone-width) instead of stretching ugly full-width.
// A `transform` on the device box makes the app's position:fixed children
// (bottom nav, SOS, stories viewer, sheets) render inside the phone, and the
// box itself is the scroll container so they stay pinned to it.
const DemoStage = ({ framed, children }) => {
  if (!framed) return children;
  return (
    <div
      data-testid="demo-desktop-stage"
      style={{
        position: "fixed", inset: 0, zIndex: 0,
        background: "radial-gradient(120% 120% at 50% 0%, #2a2d3a 0%, #0b0d14 62%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "28px",
      }}
    >
      <div
        className="no-scrollbar"
        style={{
          position: "relative",
          width: 393,
          height: "min(852px, calc(100dvh - 44px))",
          maxWidth: "100%",
          overflow: "hidden",
          background: "#FAFAFA",
          borderRadius: 46,
          boxShadow: "0 0 0 12px #0f1117, 0 0 0 14px #2a2d3a, 0 40px 100px rgba(0,0,0,0.55)",
          transform: "translateZ(0)", // KEEP: makes app's position:fixed children (nav, SOS, bottom sheets) contained by the phone frame
          ["--frame-h"]: "min(852px, calc(100dvh - 44px))",
        }}
      >
        {children}
      </div>
    </div>
  );
};


const GuestbookApp = () => {
  const { propertyId } = useParams();
  // Public demo Welcome Book → iPhone-width frame on desktop (see DemoStage).
  // Excludes marketing iframe embeds (already in a phone mockup) and host preview.
  const demoFrameWanted = (() => {
    if (typeof window === "undefined") return false;
    const p = new URLSearchParams(window.location.search);
    if (p.get("embed") === "1" || p.get("preview") === "1") return false;
    return p.get("showcase") === "1" || propertyId === "prop_demo_hostland";
  })();
  const [isWideViewport, setIsWideViewport] = useState(() => typeof window !== "undefined" && window.innerWidth >= 768);
  useEffect(() => {
    if (!demoFrameWanted) return;
    const onResize = () => setIsWideViewport(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [demoFrameWanted]);
  const framed = demoFrameWanted && isWideViewport;
  const rootScrollRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("home");
  // In the demo phone frame the app root is the scroll container, so switching
  // section must reset it to the top (otherwise the new section opens mid-page).
  useEffect(() => {
    if (framed && rootScrollRef.current) rootScrollRef.current.scrollTo({ top: 0 });
  }, [tab, framed]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [storyFeed, setStoryFeed] = useState([]);
  const [storyOpen, setStoryOpen] = useState(null);
  const [seenVersion, setSeenVersion] = useState(0);
  const storySeedRef = useRef(Math.floor(Math.random() * 100000));
  const [showProperties, setShowProperties] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [expandedUpdate, setExpandedUpdate] = useState(null);
  const [openSheet, setOpenSheet] = useState(null); // "checkin" | "wifi" | "manuals" | null
  const [lightbox, setLightbox] = useState(null); // index foto check-in ingrandita
  const [guideOpen, setGuideOpen] = useState(false); // guida dettagliata PDF/Word
  // Feedback aptico: forte sul check-in, leggero sugli altri pulsanti quadrati.
  useEffect(() => {
    if (!openSheet) return;
    vibrate(openSheet === "checkin" ? [15, 30, 15] : 12);
  }, [openSheet]);
  const [promoCount, setPromoCount] = useState(0);
  const [lang, setLang] = useState(() => {
    // Priority: 1) explicit user choice via localStorage, 2) navigator fallback.
    // Server-detected language from Accept-Language overrides #2 only if no localStorage choice
    // exists — applied via the effect below once config arrives. Keeps user preferences sticky.
    try {
      const stored = localStorage.getItem(`gb_lang_${propertyId}`);
      if (stored && ["it", "en", "fr", "es", "de"].includes(stored)) return stored;
    } catch { /* localStorage blocked: keep silent */ }
    const bl = (navigator.language || "it").slice(0, 2).toLowerCase();
    return ["it", "en", "fr", "es", "de"].includes(bl) ? bl : "it";
  });

  const t = T[lang] || T.it;
  const storyGroups = useMemo(() => groupStories(storyFeed, lang), [storyFeed, lang]);
  // Style preset → recolours Welcome Book service tiles (except weather/checkin/luggage).
  const themePreset = getPreset(config?.theme_preset);
  const usePreset = themePreset.id !== "hostland";
  const tg = (i, fallback) => (usePreset ? tileGradient(themePreset.buttons[i % themePreset.buttons.length]) : fallback);
  const tgAccent = (i, fallback) => (usePreset ? themePreset.buttons[i % themePreset.buttons.length] : fallback);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const VOICE_TXT = {
    it: { title: "Registra il tuo saluto all'host", sub: "Un vocale di un minuto, prima di ripartire" },
    en: { title: "Record your greeting to the host", sub: "A one-minute voice note, before you leave" },
    fr: { title: "Enregistre ton message pour l'hôte", sub: "Un vocal d'une minute, avant de repartir" },
    es: { title: "Graba tu saludo al anfitrión", sub: "Un audio de un minuto, antes de irte" },
    de: { title: "Nimm deinen Gruß an den Gastgeber auf", sub: "Eine Sprachnachricht von einer Minute, bevor du gehst" },
  };
  // Load the preset's Google Font (titles + text of the Welcome Book) when a non-default preset is used.
  useEffect(() => {
    if (!usePreset) return;
    const fam = themePreset.font;
    const id = `gbfont-${fam.replace(/\s+/g, "")}`;
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fam.replace(/\s+/g, "+")}:wght@400;500;600;700;800;900&display=swap`;
    document.head.appendChild(link);
  }, [usePreset, themePreset.font]);

  // Stories feed ("Scopri la Zona") — context-aware rotation from backend.
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/stories/${propertyId}/feed`, { params: { lang, seed: storySeedRef.current } });
        if (!cancelled) setStoryFeed(res.data?.stories || []);
      } catch { if (!cancelled) setStoryFeed([]); }
    })();
    return () => { cancelled = true; };
  }, [propertyId, lang]);

  useEffect(() => {
    axios.get(`${API}/guestbook/${propertyId}`).then(r => setConfig(r.data)).catch(() => setConfig(null)).finally(() => setLoading(false));
  }, [propertyId]);

  useEffect(() => {
    axios.get(`${API}/promos/public/${propertyId}`).then(r => setPromoCount(r.data.count || 0)).catch(() => setPromoCount(0));
  }, [propertyId]);

  const promoLabel = { it: "I tuoi sconti", en: "Your discounts", fr: "Vos réductions", es: "Tus descuentos", de: "Deine Rabatte" }[lang] || "I tuoi sconti";
  const promoLabelShort = { it: "Sconti", en: "Discounts", fr: "Réductions", es: "Descuentos", de: "Rabatte" }[lang] || "Sconti";

  useEffect(() => {
    document.title = config?.property_name
      ? `GuestBook — ${config.property_name} | Hostland`
      : "GuestBook Digitale | Hostland";
  }, [config?.property_name]);

  // ═══ noindex/nofollow HARD — il GuestBook contiene dati privati per gli
  // ospiti (Wi-Fi, manuali, codici) e NON deve MAI essere indicizzato dai
  // motori di ricerca. Iniettiamo il meta lato client e lo rimuoviamo al
  // dismount così le altre pagine restano indicizzabili.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive, nosnippet, noimageindex";
    meta.setAttribute("data-guestbook-noindex", "1");
    document.head.appendChild(meta);
    return () => {
      try { document.head.removeChild(meta); } catch { /* già rimosso */ }
    };
  }, []);

  // Detect whether the property's city has any upcoming event so the Esplora
  // tab can be surfaced even if the host hasn't added local POIs yet. This is
  // what enables the in-tab events ticker (data-testid="guestbook-events-ticker").
  const [hasCityEvents, setHasCityEvents] = useState(false);
  useEffect(() => {
    const city = typeof config?.property_city === "string"
      ? config.property_city
      : (config?.property_city?.nome || "");
    if (!city) { setHasCityEvents(false); return; }
    axios.get(`${API}/events/by-city`, { params: { city, limit: 1 } })
      .then(r => setHasCityEvents((r.data?.items || []).length > 0))
      .catch(() => setHasCityEvents(false));
  }, [config?.property_city]);

  // Adopt the server-detected language (from Accept-Language) on first load
  // ONLY when the user hasn't explicitly picked one yet. This is more reliable
  // than navigator.language for QR scans (e.g. tourist phones with default
  // browser locale "en-US" but Accept-Language sending "fr,en;q=0.5").
  useEffect(() => {
    if (!config?.detected_language) return;
    try {
      if (localStorage.getItem(`gb_lang_${propertyId}`)) return; // user has chosen
    } catch { /* ignore */ }
    if (["it", "en", "fr", "es", "de"].includes(config.detected_language)) {
      setLang(config.detected_language);
    }
  }, [config?.detected_language, propertyId]);

  // Persist explicit language picks so they survive across visits / QR re-scans.
  const changeLang = (newLang) => {
    setLang(newLang);
    try { localStorage.setItem(`gb_lang_${propertyId}`, newLang); } catch { /* ignore */ }
  };

  // Check if push already subscribed (local storage)
  useEffect(() => {
    if (localStorage.getItem(`push_subscribed_${propertyId}`) === "true") {
      setPushSubscribed(true);
    }
  }, [propertyId]);

  // ─── Scroll to top on tab change ─────────────────────────────────
  // Detail pages (Rules / Explore / Extra / AI Tour / WC / Manuals / Help)
  // slide in from below. If the user was scrolled halfway down when they
  // tapped a tile, the new page would enter at that same offset — feels
  // like it "opens in the middle". Always reset to the top so every tab
  // switch feels like a fresh screen. Instant scroll (not smooth) so it
  // doesn't fight the spring entry animation.
  useEffect(() => {
    try { window.scrollTo({ top: 0, behavior: "instant" }); }
    catch { window.scrollTo(0, 0); }
  }, [tab]);

  const handleSubscribePush = async () => {
    // DISABLED (2026-02): the host asked that push permission be requested
    // EXCLUSIVELY as the final step of a successful PWA install (see
    // PWAInstallPromo). This handler and the button that fires it are kept
    // in place to preserve layout, but the OneSignal slidedown is no longer
    // triggered. Once the guest installs the property webapp they'll be
    // asked automatically, so this manual toggle is redundant.
    toast(lang === "it"
      ? "Installa l'app della struttura per ricevere le notifiche 📱"
      : "Install the property app to receive notifications 📱");
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-8 h-8 border-2 border-zinc-200 border-t-zinc-900 rounded-full" />
    </div>
  );

  if (!config) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center p-6 text-center">
      <div><Info className="w-12 h-12 mx-auto mb-4 text-zinc-300" /><p className="text-lg font-bold text-zinc-400">{t.notAvailable}</p></div>
    </div>
  );

  // Apply translations for non-Italian languages
  const tr = (lang !== "it" && config.translations?.[lang]) ? config.translations[lang] : null;
  const isDemoWB = (() => { try { return new URLSearchParams(window.location.search).get("showcase") === "1" || propertyId === "prop_demo_hostland"; } catch { return propertyId === "prop_demo_hostland"; } })();

  const welcomeText = tr?.welcome_text || config.welcome_text;

  // Check-in translations
  const checkin = config.checkin ? {
    ...config.checkin,
    directions: tr?.checkin?.directions || config.checkin.directions,
    self_checkin_instructions: tr?.checkin?.self_checkin_instructions || config.checkin.self_checkin_instructions,
    extra_notes: tr?.checkin?.extra_notes || config.checkin.extra_notes,
  } : null;

  const manuals = (config.manuals || []).map((m, i) => {
    const mt = tr?.manuals?.[i];
    if (!mt) return m;
    return { ...m, title: mt.title || m.title, description: mt.description || m.description, content: mt.content || m.content,
      faqs: (m.faqs || []).map((f, fi) => ({ ...f, question: mt.faqs?.[fi]?.question || f.question, answer: mt.faqs?.[fi]?.answer || f.answer }))
    };
  });

  const places = (config.local_places || []).map((p, i) => {
    const pt = tr?.local_places?.[i];
    if (!pt) return p;
    return { ...p, why_go: pt.why_go || p.why_go };
  });

  const services = (config.extra_services || []).map((s, i) => {
    const st = tr?.extra_services?.[i];
    if (!st) return s;
    return { ...s, name: st.name || s.name, description: st.description || s.description };
  });

  const wasteMap = (config.waste_schedule || []).map((w, i) => {
    const wt = tr?.waste_schedule?.[i];
    if (!wt) return w;
    return { ...w, type: wt.type || w.type, notes: wt.notes || w.notes };
  });

  const houseRules = config.house_rules ? {
    ...config.house_rules,
    key_instructions: tr?.house_rules?.key_instructions || config.house_rules.key_instructions,
    tourist_tax_info: tr?.house_rules?.tourist_tax_info || config.house_rules.tourist_tax_info,
    custom_rules: tr?.house_rules?.custom_rules || config.house_rules.custom_rules,
  } : config.house_rules;

  const updates = (config.updates || []).map((u, i) => {
    // Mappa per id (robusto anche se l'ordine cambia); fallback per indice
    // per traduzioni salvate prima dell'introduzione dell'id.
    const ut = (tr?.updates || []).find((x) => x.id && u.id && x.id === u.id) || tr?.updates?.[i];
    if (!ut) return u;
    return { ...u, title: ut.title || u.title, text: ut.text || u.text };  });

  const hasExplore = places.length > 0 || hasCityEvents;
  const hasRules = houseRules && (houseRules.checkin_time || houseRules.checkout_time || houseRules.quiet_from || houseRules.key_instructions || houseRules.tourist_tax_info || (houseRules.custom_rules && houseRules.custom_rules.length > 0));
  const hasExtra = services.length > 0;
  const hasUseful = (config.useful_numbers || []).length > 0;

  const hasToilets = config.show_toilets_tab !== false && (config.toilets_lat || config.location_lat);
  const hasCheckin = !!(checkin && (checkin.precheckin_url || checkin.directions || checkin.location_url || checkin.self_checkin_instructions || checkin.keybox_code || checkin.keybox_internal || checkin.door_link || checkin.door_link_internal || checkin.video_url || checkin.extra_notes || checkin.checkin_time || checkin.help_phone || (Array.isArray(checkin.parking) && checkin.parking.length > 0)));
  const hasWifi = !!(config.wifi?.ssid || (Array.isArray(config.wifi?.networks) && config.wifi.networks.some((n) => n?.ssid)));
  // Any transport category with real content? Used to decide whether to
  // surface the Transports tile on the Home + the top button on Explore.
  // We also merge in host translations from `tr.transports.{cat}` (same flat
  // path structure the backend uses) so descriptions/titles render in the
  // guest's language when a non-Italian translation is cached.
  const transports = (() => {
    const src = config.transports || {};
    const tt = tr?.transports;
    if (!tt) return src;
    const out = {};
    for (const [catId, v] of Object.entries(src)) {
      const tv = tt[catId];
      if (!tv || !v || typeof v !== "object") { out[catId] = v; continue; }
      const merged = { ...v };
      if (Array.isArray(v.items)) {
        merged.items = v.items.map((it, i) => ({
          ...it,
          name:        tv.items?.[i]?.name        || it.name,
          description: tv.items?.[i]?.description || it.description,
        }));
      }
      if (Array.isArray(v.tabs)) {
        merged.tabs = v.tabs.map((tab, ti) => ({
          ...tab,
          title: tv.tabs?.[ti]?.title || tab.title,
          items: (tab.items || []).map((it, i) => ({
            ...it,
            name:        tv.tabs?.[ti]?.items?.[i]?.name        || it.name,
            description: tv.tabs?.[ti]?.items?.[i]?.description || it.description,
          })),
        }));
      }
      if (v.sub && typeof v.sub === "object") {
        merged.sub = {};
        for (const [subId, sv] of Object.entries(v.sub)) {
          const tsv = tv.sub?.[subId];
          merged.sub[subId] = {
            ...sv,
            items: (sv.items || []).map((it, i) => ({
              ...it,
              name:        tsv?.items?.[i]?.name        || it.name,
              description: tsv?.items?.[i]?.description || it.description,
            })),
          };
        }
      }
      if (Array.isArray(v.list)) {
        merged.list = v.list.map((c, li) => ({
          ...c,
          title: tv.list?.[li]?.title || c.title,
          items: (c.items || []).map((it, i) => ({
            ...it,
            name:        tv.list?.[li]?.items?.[i]?.name        || it.name,
            description: tv.list?.[li]?.items?.[i]?.description || it.description,
          })),
        }));
      }
      out[catId] = merged;
    }
    return out;
  })();
  const hasTransports = TRANSPORT_CATEGORIES.some((cat) => {
    const v = transports[cat.id];
    if (!v || v.active === false) return false;
    if (cat.shape === "list")   return (v.items || []).some((it) => it?.name);
    if (cat.shape === "tabs")   return (v.tabs || []).some((t) => (t.items || []).some((it) => it?.name));
    if (cat.shape === "sub")    return cat.subs.some((s) => {
      const sv = (v.sub || {})[s.id];
      return sv && sv.active !== false && (sv.items || []).some((it) => it?.name);
    });
    if (cat.shape === "custom") return (v.list || []).some((c) => c.active !== false && (c.items || []).some((it) => it?.name));
    return false;
  });

  // Luggage storage — only surfaced when the host added at least one named
  // structure. Merges host translations (tr.luggage.items) when available.
  const luggageItems = (() => {
    const src = (config.luggage?.items || []).filter((it) => it?.name);
    const tv = tr?.luggage?.items;
    const merged = tv ? src.map((it, i) => ({
      ...it,
      description: tv[i]?.description || it.description,
      convention: tv[i]?.convention || it.convention,
    })) : src;
    return [...merged].sort((a, b) => (Number(a.distance_m) || Infinity) - (Number(b.distance_m) || Infinity));
  })();
  const hasLuggage = luggageItems.length > 0;

  // ═══ TileButton — glass gradient tile, iOS-style centered layout.
  // Larger central icon (glass container) with an optional micro-icon
  // badge on the top-right that carries a subtle "tech" animation (rotate,
  // bob, pulse). Text is centered under the icon so every tile looks the
  // same regardless of subtitle length. Animations are very light: single
  // motion.div per tile, low FPS keyframes, GPU-friendly transforms only.
  const TileButton = ({
    onClick,
    icon: Icon,
    iconAnim,
    iconTransition,
    title,
    subtitle,
    gradient,
    span = 1,
    testId,
  }) => (
    <motion.button
      whileTap={{ scale: 0.97 }}
      whileHover={{ y: -2 }}
      onClick={onClick}
      data-testid={testId}
      className={`relative overflow-hidden rounded-[28px] text-white shadow-xl shadow-black/25 ring-1 ring-inset ring-white/20 active:shadow-md transition-shadow flex flex-col items-center justify-between text-center ${span === 2 ? "col-span-2 py-6 px-4" : "col-span-1 aspect-square p-4"}`}
      style={{ background: gradient }}
    >
      {/* Soft radial glow */}
      <span aria-hidden className="absolute -top-6 -right-6 w-28 h-28 rounded-full opacity-[0.18]"
        style={{ background: "radial-gradient(circle at center, #ffffff 0%, transparent 60%)" }} />
      <span aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 55%)" }} />

      {/* BIG animated icon — transparent bg, no glass container. Icon is
          vertically centred inside the tile so it feels intentional and
          balanced; title sits at the bottom. */}
      <motion.div
        animate={iconAnim || {}}
        transition={iconAnim ? (iconTransition || { duration: 4, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.4 }) : undefined}
        className={`relative flex items-center justify-center ${span === 2 ? "" : "flex-1 w-full"}`}
        style={{ filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.25))" }}
      >
        <Icon className={`text-white ${span === 2 ? "w-[76px] h-[76px]" : "w-[86px] h-[86px]"}`} strokeWidth={1.25} />
      </motion.div>

      {/* Title + subtitle — centered */}
      <div className="relative w-full">
        <p className={`font-black tracking-tight leading-tight uppercase ${span === 2 ? "text-[18px]" : "text-[14.5px]"}`}>{title}</p>
        {subtitle && (
          <p className={`text-white/85 font-medium leading-snug mt-0.5 ${span === 2 ? "text-[12px]" : "text-[10.5px] line-clamp-1 px-1"}`}>{subtitle}</p>
        )}
      </div>
    </motion.button>
  );

  const TILE_SUBS = {
    checkin:   { it: "Come entrare in casa",     en: "How to get in",           fr: "Comment entrer",       es: "Cómo entrar",         de: "So kommst du rein" },
    wifi:      { it: "Connetti subito",          en: "Connect instantly",       fr: "Connexion rapide",     es: "Conéctate ya",        de: "Sofort verbinden" },
    manuals:   { it: "Istruzioni casa",          en: "House instructions",      fr: "Instructions maison",  es: "Instrucciones casa",  de: "Haus-Anleitungen" },
    rules:     { it: "Orari e regole",           en: "Times & rules",           fr: "Horaires & règles",    es: "Horarios y reglas",   de: "Zeiten & Regeln" },
    explore:   { it: "Cosa vedere in zona",      en: "What to see nearby",      fr: "À voir autour",        es: "Qué ver cerca",       de: "Sehenswertes" },
    aitour:    { it: "Itinerario magico",        en: "Magic itinerary",         fr: "Itinéraire magique",   es: "Itinerario mágico",   de: "Magische Route" },
    extra:     { it: "Servizi in loco",          en: "On-site services",        fr: "Services sur place",   es: "Servicios locales",   de: "Vor-Ort-Services" },
    useful:    { it: "Contatti locali",          en: "Local contacts",          fr: "Contacts locaux",      es: "Contactos locales",   de: "Lokale Kontakte" },
    toilets:   { it: "WC nelle vicinanze",       en: "Public toilets nearby",   fr: "Toilettes proches",    es: "Aseos cercanos",      de: "WC in der Nähe" },
  };
  const sub = (k) => TILE_SUBS[k]?.[lang] || TILE_SUBS[k]?.it || "";

  const tabs = [
    { id: "home", icon: Home, label: "Home" },
    ...(hasExtra ? [{ id: "extra", icon: ShoppingBag, label: "Extra" }] : []),
    { id: "aitour", icon: Wand2, label: "AI Tour" },
    ...(hasExplore ? [{ id: "explore", icon: Compass, label: t.explore }] : []),
    ...(hasToilets ? [{ id: "toilets", icon: Bath, label: lang === "it" ? "WC" : "WC" }] : []),
    ...(hasRules ? [{ id: "rules", icon: Clock, label: t.rules }] : []),
  ];

  return (
    <DemoStage framed={framed}>
    <div
      ref={rootScrollRef}
      className="min-h-screen bg-[#FAFAFA] pb-28 overflow-x-hidden guestbook-apple"
      data-testid="guestbook-app"
      style={{
        fontFamily: usePreset
          ? `"${themePreset.font}", "Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", system-ui, sans-serif`
          : '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", system-ui, sans-serif',
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        letterSpacing: "-0.011em",
        ...(framed ? { height: "100%", minHeight: 0, overflowY: "auto", overflowX: "hidden", WebkitOverflowScrolling: "touch" } : {}),
      }}
    >
      <style>{`
        .guestbook-apple { font-feature-settings: "ss01","cv11"; }
        .guestbook-apple h1, .guestbook-apple h2, .guestbook-apple h3 {
          letter-spacing: -0.022em;
        }
        .guestbook-apple input, .guestbook-apple textarea, .guestbook-apple button {
          font-family: inherit;
        }
        /* Generous safe-area padding for content blocks */
        .guestbook-apple .gb-section { padding-left: 1.5rem; padding-right: 1.5rem; }
        @media (min-width: 640px) {
          .guestbook-apple .gb-section { padding-left: 2rem; padding-right: 2rem; }
        }
        /* Softer shadows Apple-style */
        .guestbook-apple .gb-card {
          box-shadow:
            0 1px 2px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.04);
          border-radius: 1.5rem;
        }
      `}</style>

      {/* ═══ HOME ═══ */}
      {tab === "home" && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {/* Hero di benvenuto: FOTO copertina o VIDEO (YouTube/Vimeo/Drive/mp4),
              con sfumatura bianca in basso. */}
          {(() => {
            const heroType = config.welcome_hero_type || (config.welcome_video_url ? "video" : (config.welcome_cover_photo_url ? "photo" : null));
            if (heroType === "photo" && config.welcome_cover_photo_url) {
              const src = (config.welcome_cover_photo_url.startsWith("http") || config.welcome_cover_photo_url.startsWith("/"))
                ? config.welcome_cover_photo_url
                : `${API}/files/${config.welcome_cover_photo_url}`;
              return (
                <div className="relative w-full aspect-square max-h-[52vh] overflow-hidden">
                  <img src={src} alt="Copertina" className="w-full h-full object-cover" loading="eager" fetchpriority="high" data-testid="guestbook-cover-photo" />
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#FAFAFA] to-transparent pointer-events-none" />
                </div>
              );
            }
            if (heroType !== "photo" && config.welcome_video_url) {
              const parsed = parseVideoUrl(config.welcome_video_url);
              if (!parsed) return null;
              return (
                <div className="relative w-full aspect-video max-h-[50vh] overflow-hidden">
                  {parsed.kind === "mp4" ? (
                    <video
                      src={parsed.embedUrl}
                      className="w-full h-full object-cover"
                      autoPlay muted loop playsInline preload="auto"
                      data-testid="guestbook-welcome-video"
                    />
                  ) : (
                    <iframe
                      src={parsed.embedUrl}
                      className="w-full h-full" frameBorder="0"
                      allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; autoplay" allowFullScreen
                      title="Video Benvenuto"
                      data-testid={`guestbook-welcome-embed-${parsed.kind}`} />
                  )}
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#FAFAFA] to-transparent pointer-events-none" />
                </div>
              );
            }
            return null;
          })()}

          <div className="px-6 pt-8">

            {/* Header stile Instagram: logo quadrato a sinistra, titolo + indirizzo a destra */}
            <div className="flex items-center gap-4">
              <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="shrink-0">
                <div className="h-[84px] w-[84px] rounded-[22px] overflow-hidden shadow-sm ring-1 ring-black/[0.06] flex items-center justify-center bg-white">
                  {config.logo_url ? (
                    <img
                      src={(config.logo_url.startsWith("http") || config.logo_url.startsWith("/")) ? config.logo_url : `${API}/files/${config.logo_url}`}
                      alt="Logo" className="w-full h-full object-cover" data-testid="guestbook-logo" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: "#FCD34D" }} data-testid="guestbook-logo-fallback">
                      <Home className="w-10 h-10 text-zinc-900" strokeWidth={2.1} />
                    </div>
                  )}
                </div>
              </motion.div>
              <div className="flex-1 min-w-0">
                <motion.h1 initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.1 }}
                  className="font-black text-zinc-900 tracking-tight leading-[1.06] break-words line-clamp-2"
                  style={{ fontSize: "clamp(1.5rem, 6.2vw, 2.1rem)" }} data-testid="guestbook-title">
                  {config.property_name || t.welcome}
                </motion.h1>
                {config.property_address && (
                  <motion.p initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2 }}
                    className="text-sm text-zinc-400 mt-1.5 flex items-center gap-1 font-medium min-w-0">
                    <MapPin className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{config.property_address}</span>
                  </motion.p>
                )}
              </div>
            </div>

            {/* Language Selector — bolla liquid glass, a piena larghezza */}
            <div className="mt-4 mb-2">
              <LanguageSwitcher value={lang} onChange={changeLang} />
            </div>

            {/* Weather Widget spostato dentro la tile grid (WeatherMicroTile).
                Il full detail sheet è renderizzato sotto tramite BottomSheet. */}

            {/* Live Updates — schede minimal in stile Wallet */}
            {updates?.length > 0 && (
              <motion.div variants={cardIn} custom={0} initial="hidden" animate="visible"
                className="mt-4" data-testid="live-updates">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
                  </span>
                  <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-zinc-500">
                    {lang === "it" ? "Aggiornamenti" : lang === "fr" ? "Mises à jour" : lang === "es" ? "Actualizaciones" : "Updates"}
                  </p>
                  <span className="text-[9px] text-zinc-300 font-semibold ml-auto">LIVE</span>
                  {pushSubscribed && (
                    <span className="flex items-center gap-1 bg-emerald-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full" data-testid="push-subscribed-badge">
                      <Check className="w-2.5 h-2.5" /> {lang === "it" ? "Attive" : "On"}
                    </span>
                  )}
                </div>
                <div className="relative flex flex-col">
                  {updates.slice(0, 3).map((upd, i) => {
                    const ts = upd.created_at ? new Date(upd.created_at).toLocaleDateString(lang === "it" ? "it-IT" : lang === "fr" ? "fr-FR" : lang === "es" ? "es-ES" : "en-US", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                    const first = i === 0;
                    const uid = upd.id || `upd-${i}`;
                    const isOpen = expandedUpdate === uid;
                    const anyOpen = expandedUpdate !== null;
                    const n = Math.min(updates.length, 3);
                    const img = upd.image_url ? (upd.image_url.startsWith("http") ? upd.image_url : `${API}/files/${upd.image_url}`) : null;
                    return (
                      <motion.div layout key={uid}
                        onClick={() => { if (!isOpen) vibrate(10); setExpandedUpdate(isOpen ? null : uid); }}
                        className={`rounded-2xl p-3 border cursor-pointer ${first ? "border-transparent" : "bg-white border-zinc-100"}`}
                        style={{ position: "relative", transformOrigin: "top center", zIndex: anyOpen ? (isOpen ? 50 : 1) : (n - i), boxShadow: (isOpen || (!anyOpen && first)) ? "0 18px 34px -16px rgba(0,0,0,0.35)" : "0 8px 18px -10px rgba(0,0,0,0.28)", ...(first ? { background: "linear-gradient(135deg,#F43F5E,#E11D48)" } : {}) }}
                        animate={{ marginTop: i === 0 ? 0 : (anyOpen ? 10 : -46), scale: anyOpen ? 1 : 1 - Math.min(i, 3) * 0.05 }}
                        transition={{ type: "spring", stiffness: 340, damping: 32 }}>
                        {/* Header ticket (sempre visibile) */}
                        <div className="flex items-center gap-3">
                          {img && !isOpen && (
                            <img src={img} alt="" className="w-11 h-11 rounded-xl object-cover shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                              {first && <span className="text-[8px] font-bold bg-white text-rose-600 px-1.5 py-0.5 rounded-full uppercase">{{ it: "Nuovo", en: "New", fr: "Nouveau", es: "Nuevo", de: "Neu" }[lang] || "New"}</span>}
                              {ts && <span className={`text-[10px] font-medium ${first ? "text-white/80" : "text-zinc-400"}`}>{ts}</span>}
                            </div>
                            <p className={`text-[14px] font-semibold leading-snug ${isOpen ? "" : "line-clamp-1"} ${first ? "text-white" : "text-zinc-900"}`}>{upd.title}</p>
                          </div>
                          <motion.span animate={{ rotate: isOpen ? 90 : 0 }} transition={{ duration: 0.2 }} className="shrink-0">
                            <ChevronRight className={`w-4 h-4 ${first ? "text-white/90" : "text-zinc-400"}`} />
                          </motion.span>
                        </div>

                        {/* Contenuto espanso */}
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.28, ease: "easeInOut" }}
                              className="overflow-hidden"
                              onClick={(e) => e.stopPropagation()}>
                              <div className="pt-3">
                                {img && (
                                  <img src={img} alt="" className="w-full rounded-xl object-cover mb-2.5" style={{ maxHeight: 180 }} />
                                )}
                                {(upd.event_date || upd.event_location) && (
                                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                                    {upd.event_date && (
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${first ? "bg-white/20 text-white" : "text-amber-700 bg-amber-50"}`}>
                                        <Clock className="w-2.5 h-2.5" /> {upd.event_date}
                                      </span>
                                    )}
                                    {upd.event_location && (
                                      <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${first ? "bg-white/20 text-white" : "text-amber-700 bg-amber-50"}`}>
                                        <MapPin className="w-2.5 h-2.5" /> {upd.event_location}
                                      </span>
                                    )}
                                  </div>
                                )}
                                {upd.text && <p className={`text-xs leading-relaxed ${first ? "text-white/90" : "text-zinc-600"}`}>{upd.text}</p>}
                                {upd.link && (
                                  <a href={upd.link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={`inline-flex items-center gap-1 text-[11px] font-semibold mt-2 ${first ? "text-white hover:text-white/80" : "text-amber-700 hover:text-amber-900"}`}>
                                    {lang === "it" ? "Scopri di più" : lang === "fr" ? "En savoir plus" : lang === "es" ? "Más info" : "Learn more"} <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
                {Math.min(updates.length, 3) > 1 && expandedUpdate === null && (
                  <p className="text-center text-[11px] font-medium text-zinc-400 mt-2.5" data-testid="updates-more-count">
                    +{Math.min(updates.length, 3) - 1} {lang === "fr" ? "autres" : lang === "es" ? "más" : lang === "de" ? "weitere" : lang === "en" ? "more" : "altri"}
                  </p>
                )}
              </motion.div>
            )}

            {/* ═══ Saluto di benvenuto dell'host — foto tonda con anello animato + testo ═══ */}
            {(welcomeText || config.host_name || config.host_avatar_url) && (() => {
              const accent = config.accent_color || themePreset.accent || "#d04260";
              const avatar = config.host_avatar_url
                ? ((config.host_avatar_url.startsWith("http") || config.host_avatar_url.startsWith("/"))
                    ? config.host_avatar_url
                    : `${API}/files/${config.host_avatar_url}`)
                : null;
              return (
                <motion.div
                  variants={cardIn} custom={0} initial="hidden" animate="visible"
                  className="relative mt-5 overflow-hidden rounded-[28px] p-5 border border-zinc-100 shadow-sm"
                  style={{ background: "linear-gradient(135deg,#ffffff 0%,#fbfbfd 100%)" }}
                  data-testid="welcome-greeting"
                >
                  <div className="pointer-events-none absolute -top-12 -right-10 w-40 h-40 rounded-full blur-2xl" style={{ background: `${accent}22` }} />
                  <div className="relative">
                    {avatar && (
                      <img
                        src={avatar}
                        alt={config.host_name || "Host"}
                        loading="eager"
                        className="float-right ml-4 mb-2 w-[106px] h-[106px] rounded-full object-cover shadow-md shadow-black/10"
                        style={{ border: "4px solid #FDE68A" }}
                        data-testid="welcome-host-avatar"
                      />
                    )}
                    <p className="text-[17px] leading-snug text-zinc-900 font-extrabold" data-testid="welcome-host-line">
                      {config.host_name ? `${config.host_name} ${t.welcomesYou}` : `${t.welcome}!`}
                    </p>
                    {welcomeText && (
                      <p className="mt-2 text-[14px] text-zinc-600 leading-relaxed whitespace-pre-line font-normal">{welcomeText}</p>
                    )}
                  </div>
                </motion.div>
              );
            })()}

            {/* Alert meteo avverso RIMOSSO (2026-09) su richiesta: spaventava gli ospiti.
                Il meteo normale resta disponibile tramite la tile "Meteo". */}

            {/* ═══ TILE GRID — modern iOS-style shortcuts ═══ */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
              className="mt-5 grid grid-cols-2 gap-3"
              data-testid="home-tile-grid"
            >
              {hasCheckin && (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  whileHover={{ y: -2 }}
                  animate={{ scale: [1, 1.015, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                  onClick={() => setOpenSheet("checkin")}
                  data-testid="tile-checkin"
                  className="col-span-2 relative overflow-hidden rounded-[28px] text-white flex items-center gap-4 p-3.5 pr-5 ring-1 ring-inset ring-white/25 active:shadow-md text-left"
                  style={{ background: "linear-gradient(135deg,#ff4d2e 0%,#ef2b1d 45%,#c81208 100%)", boxShadow: "0 12px 34px -6px rgba(220,38,38,0.6)" }}
                >
                  {/* Soft radial glow + top sheen */}
                  <span aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-[0.18]"
                    style={{ background: "radial-gradient(circle at center,#ffffff 0%,transparent 60%)" }} />
                  <span aria-hidden className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg,rgba(255,255,255,0.14) 0%,rgba(255,255,255,0) 55%)" }} />
                  {/* Welcome badge — pulsing dot, top-right */}
                  <div className="absolute top-2.5 right-3 z-20 flex items-center gap-1" data-testid="tile-checkin-welcome">
                    <motion.span
                      className="w-2 h-2 rounded-full bg-white"
                      style={{ boxShadow: "0 0 6px rgba(255,255,255,0.9)" }}
                      animate={{ opacity: [1, 0.25, 1], scale: [1, 0.8, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                    />
                    <span className="text-[10px] font-black tracking-wider text-white/95 leading-none lowercase">welcome</span>
                  </div>
                  {/* Animated key opening a door */}
                  <div className="relative shrink-0 w-[84px] h-[84px] rounded-2xl overflow-hidden ring-1 ring-white/25 bg-white/15 flex items-center justify-center">
                    <svg viewBox="0 0 64 64" width="62" height="62" fill="none" aria-hidden="true">
                      {/* door */}
                      <rect x="14" y="7" width="30" height="50" rx="4" fill="#ffffff" />
                      <rect x="19" y="12" width="20" height="18" rx="2.5" fill="#f3c9c4" opacity="0.55" />
                      <rect x="19" y="34" width="20" height="18" rx="2.5" fill="#f3c9c4" opacity="0.55" />
                      {/* lock plate + keyhole on the door */}
                      <circle cx="39" cy="33" r="3.4" fill="#7a0d05" />
                      {/* key inserted in the lock — turns around the keyhole */}
                      <motion.g
                        style={{ transformBox: "fill-box", transformOrigin: "0% 50%" }}
                        animate={{ rotate: [0, 82, 82, 0, 0] }}
                        transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut", times: [0, 0.35, 0.6, 0.9, 1] }}
                      >
                        <rect x="39" y="31.8" width="14" height="2.6" rx="1.3" fill="#ffd24d" />
                        <rect x="40.5" y="34.4" width="2.4" height="3.4" rx="1" fill="#ffd24d" />
                        <rect x="44" y="34.4" width="2.4" height="2.6" rx="1" fill="#ffd24d" />
                        <circle cx="55" cy="33" r="4.2" fill="none" stroke="#ffd24d" strokeWidth="2.6" />
                      </motion.g>
                    </svg>
                  </div>
                  {/* Title + subtitle (translated), left-aligned */}
                  <div className="relative flex-1 min-w-0">
                    <p className="font-black tracking-tight leading-none text-[26px] uppercase">{t.checkin}</p>
                    <p className="text-white/85 font-medium leading-snug mt-0 text-[14px] truncate">{sub("checkin")}</p>
                  </div>
                </motion.button>
              )}
              {/* ═══ Slider Eventi — tra Check-in e i tasti quadrati ═══
                  Mostrato SOLO se ci sono eventi (di città o aggiunti a mano).
                  A tutta larghezza (col-span-2), stessa spaziatura gap delle box. */}
              {hasCityEvents && (
                <EventsTicker
                  apiBaseUrl={BACKEND_URL}
                  city={typeof config.property_city === "string" ? config.property_city : (config.property_city?.nome || "")}
                  propertyId={config.property_id || ""}
                  theme={{ text: "#18181b", sub: "#71717A", border: "#e4e4e7", bg: "#fff" }}
                  flush
                  className="col-span-2"
                />
              )}
              {/* Meteo micro-tile: fetches Open-Meteo directly, opens the full
                  WeatherWidget in a bottom sheet on tap. */}
              <WeatherMicroTile
                lat={config.weather_lat || config.location_lat}
                lng={config.weather_lng || config.location_lng}
                lang={lang}
                onClick={() => setOpenSheet("weather")}
              />
              {hasWifi && (
                <TileButton
                  onClick={() => setOpenSheet("wifi")}
                  icon={Wifi}
                  iconAnim={{ scale: [1, 1.08, 1] }}
                  microIcon={Signal}
                  microAnim={{ opacity: [1, 0.35, 1], scale: [1, 1.15, 1] }}
                  microColor="#CBD5E1"
                  title={t.wifi}
                  subtitle={sub("wifi")}
                  gradient="linear-gradient(135deg,#475569 0%,#1F2937 55%,#0F172A 100%)"
                  testId="tile-wifi"
                />
              )}
              {manuals.length > 0 && (
                <TileButton
                  onClick={() => setOpenSheet("manuals")}
                  icon={BookOpen}
                  iconAnim={{ y: [0, -2, 0] }}
                  microIcon={BookMarked}
                  microAnim={{ y: [0, -3, 0], rotate: [-6, 6, -6] }}
                  microColor="#BE123C"
                  title={t.homeManuals}
                  subtitle={`${manuals.length} ${lang === "it" ? "istruzioni" : lang === "fr" ? "instructions" : lang === "es" ? "instrucciones" : lang === "de" ? "Anleitungen" : "instructions"}`}
                  gradient={tg(1, "linear-gradient(135deg,#FB7185 0%,#F43F5E 55%,#BE123C 100%)")}
                  testId="tile-manuals"
                />
              )}
              {hasRules && (
                <TileButton
                  onClick={() => setOpenSheet("rules")}
                  icon={Clock}
                  iconAnim={{ scale: [1, 1.05, 1] }}
                  title={t.rules}
                  subtitle={sub("rules")}
                  gradient={tg(2, "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 55%,#5B21B6 100%)")}
                  testId="tile-rules"
                />
              )}
              {hasExplore && (
                <TileButton
                  onClick={() => setTab("explore")}
                  icon={Compass}
                  iconAnim={{ rotate: [0, 360] }}
                  iconTransition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                  title={t.explore}
                  subtitle={sub("explore")}
                  gradient={tg(3, "linear-gradient(135deg,#34D399 0%,#10B981 55%,#047857 100%)")}
                  testId="tile-explore"
                />
              )}
              {promoCount > 0 && (
                <TileButton
                  onClick={() => setTab("sconti")}
                  icon={BadgePercent}
                  iconAnim={{ rotate: [0, -10, 10, 0] }}
                  microIcon={BadgePercent}
                  microAnim={{ scale: [1, 1.15, 1] }}
                  microColor="#FF3B30"
                  title={promoLabel}
                  subtitle={lang === "en" ? "Exclusive deals" : lang === "fr" ? "Offres exclusives" : lang === "es" ? "Ofertas exclusivas" : lang === "de" ? "Exklusive Angebote" : "Offerte esclusive"}
                  gradient={tg(4, "linear-gradient(135deg,#FF6B60 0%,#FF3B30 55%,#D42A20 100%)")}
                  testId="tile-sconti"
                />
              )}
              {hasTransports && (
                <TileButton
                  onClick={() => setTab("transports")}
                  icon={TRANSPORTS_ACCENT.icon}
                  iconAnim={{ x: [0, 2, 0, -2, 0] }}
                  microIcon={Ticket}
                  microAnim={{ y: [0, -3, 0], rotate: [-8, 8, -8] }}
                  microColor="#0369A1"
                  title={TRANSPORTS_TITLE[lang] || TRANSPORTS_TITLE.it}
                  subtitle={TRANSPORTS_SUBTITLE[lang] || TRANSPORTS_SUBTITLE.it}
                  gradient={tg(5, TRANSPORTS_ACCENT.gradient)}
                  testId="tile-transports"
                />
              )}
              <TileButton
                onClick={() => setTab("aitour")}
                icon={Wand2}
                iconAnim={{ rotate: [0, -10, 10, 0] }}
                microIcon={Sparkles}
                microAnim={{ rotate: [0, 360], scale: [1, 1.2, 1] }}
                microTransition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                microColor="#701A75"
                title="AI Tour"
                subtitle={sub("aitour")}
                gradient={tg(6, "linear-gradient(135deg,#E879F9 0%,#C026D3 55%,#701A75 100%)")}
                testId="tile-aitour"
              />
              {hasExtra && (
                <TileButton
                  onClick={() => setTab("extra")}
                  icon={ShoppingBag}
                  iconAnim={{ y: [0, -2, 0] }}
                  microIcon={Sparkles}
                  microAnim={{ rotate: [0, 360] }}
                  microColor="#B45309"
                  title={t.extraServices}
                  subtitle={sub("extra")}
                  gradient={tg(7, "linear-gradient(135deg,#FBBF24 0%,#F59E0B 55%,#B45309 100%)")}
                  testId="tile-extra"
                />
              )}
              {hasUseful && (
                <TileButton
                  onClick={() => setOpenSheet("useful")}
                  icon={Phone}
                  iconAnim={{ rotate: [0, -12, 12, -6, 6, 0] }}
                  iconTransition={{ duration: 2, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
                  microIcon={Signal}
                  microAnim={{ opacity: [1, 0.35, 1], scale: [1, 1.25, 1] }}
                  microTransition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                  microColor="#0F172A"
                  title={lang === "it" ? "Numeri" : lang === "fr" ? "Numéros" : lang === "es" ? "Números" : lang === "de" ? "Nummern" : "Numbers"}
                  subtitle={sub("useful")}
                  gradient={tg(8, "linear-gradient(135deg,#475569 0%,#1F2937 55%,#0F172A 100%)")}
                  testId="tile-useful"
                />
              )}
              {hasToilets && (
                <TileButton
                  onClick={() => setTab("toilets")}
                  icon={Bath}
                  microIcon={Droplet}
                  microAnim={{ y: [0, 4, 0], opacity: [1, 0.4, 1] }}
                  microColor="#BAE6FD"
                  title="WC"
                  subtitle={sub("toilets")}
                  gradient="linear-gradient(135deg,#38BDF8 0%,#0EA5E9 55%,#0284C7 100%)"
                  testId="tile-toilets"
                />
              )}
              {/* ═══ Pulsante personalizzato dell'host (tile quadrato) ═══ */}
              {config.custom_button?.enabled && config.custom_button?.link && (() => {
                const cb = config.custom_button;
                const CbIcon = CUSTOM_ICONS[cb.icon] || CUSTOM_ICONS.sparkles;
                const title = (tr?.custom_button?.title) || cb.title || "";
                const subtitle = (tr?.custom_button?.subtitle) || cb.subtitle || "";
                return (
                  <motion.a
                    href={cb.link}
                    target={cb.link.startsWith("tel:") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    whileTap={{ scale: 0.97 }} whileHover={{ y: -2 }}
                    data-testid="tile-custom-button"
                    className="col-span-1 aspect-square relative overflow-hidden rounded-[28px] text-white shadow-xl shadow-black/25 ring-1 ring-inset ring-white/20 flex flex-col items-center justify-between text-center p-4"
                    style={{ background: cb.gradient || "linear-gradient(135deg,#6366F1 0%,#4F46E5 55%,#3730A3 100%)" }}
                  >
                    <span aria-hidden className="absolute -top-8 -right-8 w-28 h-28 rounded-full opacity-[0.16]" style={{ background: "radial-gradient(circle at center,#fff 0%,transparent 60%)" }} />
                    <div className="relative flex-1 flex items-center justify-center">
                      {cb.iconSvg
                        ? <span className="w-[70px] h-[70px] [&>svg]:w-full [&>svg]:h-full [&>svg]:fill-white [&>svg]:stroke-white" dangerouslySetInnerHTML={{ __html: cb.iconSvg }} />
                        : <CbIcon className="w-[70px] h-[70px]" strokeWidth={1.25} />}
                    </div>
                    <div className="relative w-full">
                      <p className="font-black tracking-tight leading-tight text-[15px] uppercase truncate">{title}</p>
                      {subtitle && <p className="text-white/85 font-medium leading-snug text-[11px] truncate">{subtitle}</p>}
                    </div>
                  </motion.a>
                );
              })()}

              {/* ═══ Placeholder "Crea il tuo pulsante" — SOLO nella demo ═══ */}
              {isDemoWB && !(config.custom_button?.enabled && config.custom_button?.link) && (
                <div
                  data-testid="tile-custom-placeholder"
                  className="col-span-1 aspect-square rounded-[28px] border-2 border-dashed border-zinc-300 bg-zinc-50/60 flex flex-col items-center justify-center text-center p-4 gap-2"
                >
                  <span className="w-11 h-11 rounded-2xl border-2 border-dashed border-zinc-300 flex items-center justify-center text-zinc-400">
                    <Plus className="w-6 h-6" strokeWidth={2.2} />
                  </span>
                  <p className="text-[12px] font-black text-zinc-500 leading-tight uppercase tracking-wide">Crea il tuo<br />pulsante</p>
                  <p className="text-[10px] text-zinc-400 leading-snug">Personalizzabile dal pannello</p>
                </div>
              )}

              {/* ═══ Deposito Bagagli — full-width tile (Check-in style, orange).
                  Shown only when the host configured at least one structure. ═══ */}
              {hasLuggage && (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  whileHover={{ y: -2 }}
                  onClick={() => setTab("luggage")}
                  data-testid="tile-luggage"
                  className="col-span-2 relative overflow-hidden rounded-[28px] text-white flex items-center gap-4 p-3.5 pr-5 shadow-xl shadow-orange-950/25 ring-1 ring-inset ring-white/20 active:shadow-md transition-shadow text-left"
                  style={{ background: LUGGAGE_ACCENT.gradient }}
                >
                  <span aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-[0.16]"
                    style={{ background: "radial-gradient(circle at center,#ffffff 0%,transparent 60%)" }} />
                  <span aria-hidden className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(180deg,rgba(255,255,255,0.12) 0%,rgba(255,255,255,0) 55%)" }} />
                  <div className="relative shrink-0 w-[84px] h-[84px] rounded-2xl overflow-hidden ring-1 ring-white/25 bg-white/15 flex items-center justify-center">
                    <svg viewBox="0 0 64 64" width="60" height="60" fill="none" aria-hidden="true">
                      {/* floor line */}
                      <rect x="6" y="55" width="52" height="2.4" rx="1.2" fill="#ffffff" opacity="0.4" />
                      {/* suitcases already placed */}
                      <g fill="#ffffff" opacity="0.9">
                        <rect x="7" y="34" width="15" height="21" rx="3" />
                        <path d="M11 34v-3a3 3 0 016 0v3" fill="none" stroke="#ffffff" strokeWidth="2" opacity="0.9" />
                        <rect x="9" y="41" width="11" height="2.4" fill="#b45309" opacity="0.55" />
                      </g>
                      <g fill="#ffffff" opacity="0.9">
                        <rect x="43" y="30" width="16" height="25" rx="3" />
                        <path d="M47 30v-3.5a4 4 0 018 0V30" fill="none" stroke="#ffffff" strokeWidth="2" opacity="0.9" />
                        <rect x="45" y="38" width="12" height="2.6" fill="#b45309" opacity="0.55" />
                      </g>
                      {/* suitcase being placed — drops down into the middle */}
                      <motion.g
                        animate={{ y: [-26, 0, 0, -26], opacity: [0, 1, 1, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", times: [0, 0.4, 0.85, 1] }}
                      >
                        <rect x="24" y="27" width="17" height="28" rx="3.2" fill="#ffffff" />
                        <path d="M28.5 27v-4a4 4 0 018 0v4" fill="none" stroke="#ffffff" strokeWidth="2.2" />
                        <rect x="26" y="37" width="13" height="2.8" fill="#b45309" opacity="0.6" />
                        <circle cx="32.5" cy="46" r="2" fill="#b45309" opacity="0.5" />
                      </motion.g>
                    </svg>
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <p className="font-black tracking-tight leading-[0.92] text-[24px] uppercase">{LUGGAGE_TITLE[lang] || LUGGAGE_TITLE.it}</p>
                    <p className="text-white/85 font-medium leading-snug mt-1 text-[14px] truncate">{LUGGAGE_SUBTITLE[lang] || LUGGAGE_SUBTITLE.it}</p>
                  </div>
                  <ChevronRight className="relative w-5 h-5 text-white/85 shrink-0" strokeWidth={2.4} />
                </motion.button>
              )}
              {/* ═══ Voci della Casa — bottone rosso pulsante, stesse dimensioni del Deposito Bagagli ═══ */}
              {config.voice_reviews_enabled && (
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  whileHover={{ y: -2 }}
                  onClick={() => setVoiceOpen(true)}
                  data-testid="tile-voice-review"
                  className="col-span-2 relative overflow-hidden rounded-[28px] text-white flex items-center gap-4 p-3.5 pr-5 ring-1 ring-inset ring-white/20 text-left"
                  style={{ background: "linear-gradient(135deg,#ef4444 0%,#dc2626 100%)", boxShadow: "0 10px 32px -4px rgba(220,38,38,0.55)" }}
                >
                  <span aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-[0.16]" style={{ background: "radial-gradient(circle at center,#ffffff 0%,transparent 60%)" }} />
                  <div className="relative shrink-0 w-[72px] h-[72px] rounded-2xl overflow-hidden ring-1 ring-white/25 bg-white/15 flex items-center justify-center">
                    <motion.span animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
                      <svg viewBox="0 0 64 60" width="42" height="40" fill="none" aria-hidden="true">
                        {/* ometto */}
                        <circle cx="17" cy="17" r="8.5" fill="#fff" />
                        <path d="M4 48c0-8 5.8-14.5 13-14.5S30 40 30 48v3H4v-3z" fill="#fff" />
                        {/* vocale che esce dalla bocca: bla bla bla */}
                        <motion.text x="31" y="16" fontFamily="Inter, system-ui, sans-serif" fontSize="12.5" fontWeight="800" fill="#fff"
                          animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.4, repeat: Infinity, times: [0, 0.2, 1] }}>bla</motion.text>
                        <motion.text x="42" y="30" fontFamily="Inter, system-ui, sans-serif" fontSize="12.5" fontWeight="800" fill="#fff"
                          animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.25, times: [0, 0.2, 1] }}>bla</motion.text>
                        <motion.text x="31" y="44" fontFamily="Inter, system-ui, sans-serif" fontSize="12.5" fontWeight="800" fill="#fff"
                          animate={{ opacity: [0.35, 1, 0.35] }} transition={{ duration: 1.4, repeat: Infinity, delay: 0.5, times: [0, 0.2, 1] }}>bla</motion.text>
                        {/* cuoricini che salgono tra i bla bla bla */}
                        <motion.path d="M52 31 C48.5 27.5 49 24.5 52 25.8 C55 24.5 55.5 27.5 52 31 Z" fill="#ff9fb0"
                          animate={{ y: [0, -6, 0], opacity: [0.25, 1, 0.25], scale: [0.85, 1.1, 0.85] }}
                          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }} />
                        <motion.path d="M45 12.5 C42.2 9.7 42.6 7.3 45 8.4 C47.4 7.3 47.8 9.7 45 12.5 Z" fill="#ffc2cc"
                          animate={{ y: [0, -7, 0], opacity: [0.2, 0.95, 0.2], scale: [0.8, 1.05, 0.8] }}
                          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut", delay: 0.7 }} />
                        <motion.path d="M39 46.8 C36 43.8 36.4 41.2 39 42.4 C41.6 41.2 42 43.8 39 46.8 Z" fill="#ffb3c1"
                          animate={{ y: [0, -6, 0], opacity: [0.2, 0.9, 0.2], scale: [0.8, 1.08, 0.8] }}
                          transition={{ duration: 1.9, repeat: Infinity, ease: "easeInOut", delay: 1.1 }} />
                      </svg>
                    </motion.span>
                  </div>
                  <div className="relative flex-1 min-w-0">
                    <p className="font-black tracking-tight leading-[1.12] text-[18px] uppercase">{(VOICE_TXT[lang] || VOICE_TXT.it).title}</p>
                    <p className="text-white/90 font-medium leading-snug mt-1 text-[12.5px]">{(VOICE_TXT[lang] || VOICE_TXT.it).sub}</p>
                  </div>
                  <ChevronRight className="relative w-5 h-5 text-white/85 shrink-0" strokeWidth={2.4} />
                </motion.button>
              )}
            </motion.div>

            <VoiceRecorder
              propertyId={propertyId}
              open={voiceOpen}
              onClose={() => setVoiceOpen(false)}
              lang={lang}
              accent={config.accent_color || themePreset.accent || "#d04260"}
            />

            {/* Tip Button */}
            {config.tip_link && (
              <motion.a href={config.tip_link} target="_blank" rel="noopener noreferrer"
                variants={cardIn} custom={4} initial="hidden" animate="visible"
                whileTap={{ scale: 0.96 }}
                className="block mt-4"
                data-testid="tip-button">
                <div className="bg-amber-400 hover:bg-amber-500 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm shadow-amber-500/25 transition-colors">
                  <div className="w-10 h-10 bg-white/95 rounded-xl flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                      <circle cx="12" cy="12" r="10" fill="#FBBF24"/>
                      <path d="M12 6v8m-3-3h6" stroke="#92400E" strokeWidth="2.5" strokeLinecap="round"/>
                      <circle cx="12" cy="18" r="1.2" fill="#92400E"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-900 font-black text-[13px] leading-tight truncate">{t.tipTitle}</p>
                    <p className="text-zinc-900/60 text-[10px] font-semibold leading-tight truncate">{t.tipBtn}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-900/50 shrink-0" strokeWidth={2.4} />
                </div>
              </motion.a>
            )}

            {/* Google Review Button */}
            {config.google_review_link && (
              <motion.a href={config.google_review_link} target="_blank" rel="noopener noreferrer"
                variants={cardIn} custom={5} initial="hidden" animate="visible"
                whileTap={{ scale: 0.96 }}
                className="block mt-2"
                data-testid="google-review-btn">
                <div className="bg-[#DB4437] rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm shadow-red-500/25 hover:bg-[#C53929] transition-colors">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.97 10.97 0 001 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-0.5 mb-0.5">
                      {[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 text-amber-300 fill-amber-300" />)}
                    </div>
                    <p className="text-white font-black text-[13px] leading-tight truncate">{lang === "it" ? "Lascia una recensione" : lang === "fr" ? "Laissez un avis" : lang === "es" ? "Deja una reseña" : "Leave a review"}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-white/70 shrink-0" strokeWidth={2.4} />
                </div>
              </motion.a>
            )}

            {/* Direct Booking CTA */}
            {config.host_properties?.length > 0 && (
              <motion.div variants={cardIn} custom={6} initial="hidden" animate="visible"
                className="mt-2 bg-emerald-500 rounded-2xl overflow-hidden shadow-sm shadow-emerald-500/25" data-testid="direct-booking-cta">
                <button onClick={() => setShowProperties(!showProperties)}
                  className="w-full px-4 py-3 flex items-center gap-3 text-left text-white">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
                      <rect x="3" y="4" width="18" height="16" rx="3" fill="#10B981"/>
                      <path d="M8 10l3 3 5-6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-black leading-tight truncate">{t.directBooking}</p>
                    <p className="text-[10px] text-white/75 font-semibold leading-tight truncate">{t.directBookingDesc}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 text-white/70 shrink-0 ${showProperties ? "rotate-180" : ""}`} strokeWidth={2.4} />
                </button>
                <AnimatePresence>
                  {showProperties && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}
                      className="bg-white rounded-t-2xl">
                      {config.host_properties.map((hp) => (
                        <a key={hp.property_id} href={`/property/${hp.property_id}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-3 p-4 border-b border-zinc-50 last:border-0 hover:bg-zinc-50 transition-colors"
                          data-testid={`direct-prop-${hp.property_id}`}>
                          {hp.thumb ? (
                            <img src={`${API}/files/${hp.thumb}`} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-emerald-400" /></div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-zinc-900 truncate">{hp.name}</p>
                            {hp.address && <p className="text-[10px] text-zinc-400 truncate">{hp.address}</p>}
                          </div>
                          <span className="bg-emerald-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-full shrink-0">{t.viewProperty}</span>
                        </a>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {/* Rimani in contatto — email opt-in */}
            <StayInTouch propertyId={propertyId} lang={lang} />

            {/* ═══ Made with Hostland — banner in fondo alla Home ═══ */}
            <MadeWithHostland lang={lang} propertyId={propertyId} />
          </div>
        </motion.div>
      )}

      {/* ═══ MANUALS ═══ */}
      {tab === "sconti" && (
        <GuestPromos propertyId={propertyId} lang={lang} onBack={() => setTab("home")} />
      )}

      {tab === "manuals" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight"><Typewriter text={t.homeManuals} /></h2>
          </div>
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }} className="space-y-3">
            {manuals.length > 0 ? manuals.map((m, i) => <ManualCard key={m.id || i} manual={m} index={i} />) : (
              <div className="text-center py-16"><BookOpen className="w-10 h-10 mx-auto mb-3 text-zinc-200" /><p className="text-sm text-zinc-400">{t.noManuals}</p></div>
            )}
          </motion.div>
        </motion.div>
      )}

      {/* ═══ AI TOUR ═══ pagina dedicata (tab bacchetta magica) */}
      {tab === "aitour" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6 pb-10">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100" data-testid="aitour-back">
              <ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" />
            </button>
            <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-fuchsia-500" />
              <Typewriter text={AI_TOUR_LABELS[lang]?.title || AI_TOUR_LABELS.it.title} />
            </h2>
          </div>
          <AiTourCard
            propertyId={propertyId}
            city={typeof config?.property_city === "string" ? config.property_city : (config?.property_city?.nome || (lang === "it" ? "la tua città" : "your city"))}
            lang={lang}
          />
        </motion.div>
      )}

      {/* ═══ EXTRA SERVICES ═══ */}
      {tab === "extra" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6 pb-10">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight"><Typewriter text={t.extraServices} /></h2>
          </div>

          <p className="text-sm text-zinc-400 mb-5">{t.extraServicesDisclaimer}</p>

          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.08 } } }} className="space-y-3">
            {services.map((s, i) => <ServiceCard key={i} service={s} index={i} t={t} extraContacts={config.extra_contacts} />)}
          </motion.div>

          {services.length === 0 && (
            <div className="text-center py-16">
              <ShoppingBag className="w-12 h-12 text-zinc-200 mx-auto mb-3" />
              <p className="text-zinc-400 font-bold text-sm">{lang === "it" ? "Nessun servizio extra disponibile" : "No extra services available"}</p>
            </div>
          )}
        </motion.div>
      )}

      {/* ═══ NUMERI UTILI — TAB DEDICATA ═══ */}

      {/* ═══ TRASPORTI LOCALI ═══ Full page, one accordion card per active
          category. Same accent colours as the admin, so hosts recognise
          what they configured. Only active + populated categories render. */}
      {tab === "transports" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6 pb-10">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100" data-testid="transports-back-btn"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <div className="min-w-0">
              <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight leading-tight truncate">
                <Typewriter text={TRANSPORTS_TITLE[lang] || TRANSPORTS_TITLE.it} />
              </h2>
              <p className="text-[11px] text-zinc-400 font-medium">{TRANSPORTS_SUBTITLE[lang] || TRANSPORTS_SUBTITLE.it}</p>
            </div>
          </div>

          <div className="space-y-3">
            {TRANSPORT_CATEGORIES.map((cat) => {
              const v = transports[cat.id];
              if (!v || v.active === false) return null;

              // Extract renderable items depending on shape
              const groups = []; // [{ title?, items: [] }]
              if (cat.shape === "list") {
                const items = (v.items || []).filter((it) => it?.name);
                if (items.length) groups.push({ items });
              } else if (cat.shape === "tabs") {
                (v.tabs || []).forEach((t) => {
                  const items = (t.items || []).filter((it) => it?.name);
                  if (items.length) groups.push({ title: t.title, items });
                });
              } else if (cat.shape === "sub") {
                cat.subs.forEach((s) => {
                  const sv = (v.sub || {})[s.id];
                  if (!sv || sv.active === false) return;
                  const items = (sv.items || []).filter((it) => it?.name);
                  if (items.length) groups.push({ title: s.labels[lang] || s.labels.it, icon: s.icon, items });
                });
              } else if (cat.shape === "custom") {
                (v.list || []).forEach((c) => {
                  if (c.active === false) return;
                  const items = (c.items || []).filter((it) => it?.name);
                  if (items.length) groups.push({ title: c.title, items });
                });
              }
              if (!groups.length) return null;

              return (
                <TransportCategoryCard key={cat.id} cat={cat} groups={groups} lang={lang} />
              );
            })}
          </div>
        </motion.div>
      )}

      {/* ═══ DEPOSITO BAGAGLI ═══ */}
      {tab === "luggage" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6 pb-10" data-testid="luggage-page">
          <div className="flex items-center gap-3 mb-5">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100" data-testid="luggage-back-btn"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <div className="min-w-0">
              <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight leading-tight truncate">
                <Typewriter text={LUGGAGE_TITLE[lang] || LUGGAGE_TITLE.it} />
              </h2>
              <p className="text-[11px] text-zinc-400 font-medium">{LUGGAGE_SUBTITLE[lang] || LUGGAGE_SUBTITLE.it}</p>
            </div>
          </div>
          <div className="space-y-3">
            {luggageItems.map((it, i) => (
              <LuggageStructureCard key={it.id || i} item={it} lang={lang} index={i} />
            ))}
          </div>
        </motion.div>
      )}

      {/* ═══ EXPLORE ═══ */}
      {tab === "explore" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6 overflow-x-hidden">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight"><Typewriter text={t.explore} /></h2>
          </div>

          {/* ═══ Scopri la Zona — riga di Storie (entry point).
              Sostituisce ricerca, slider eventi e pulsante trasporti. ═══ */}
          {storyFeed.length > 0 && (
            <div className="mb-6" data-testid="stories-section">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-orange-500" strokeWidth={2.4} />
                <p className="text-[13px] font-black text-zinc-800 tracking-tight">{STORIES_TITLE[lang] || STORIES_TITLE.it}</p>
              </div>
              <StoriesRow
                stories={storyFeed}
                lang={lang}
                propertyId={propertyId}
                seenVersion={seenVersion}
                onOpen={(i) => setStoryOpen({ stories: storyGroups[i].stories, originId: storyGroups[i].cover.story_id })}
              />
            </div>
          )}

          {/* Category Filter (barra di ricerca rimossa su richiesta) */}
          {places.length > 0 && (() => {
            const cats = [...new Set(places.map(p => p.category).filter(Boolean))];
            if (cats.length <= 1) return null;
            return (
              <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide mb-5">
                <button onClick={() => setFilterCat("")}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all shrink-0 ${!filterCat ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 border border-zinc-100"}`}
                  data-testid="filter-all">
                  {lang === "it" ? "Tutti" : lang === "en" ? "All" : lang === "fr" ? "Tous" : "Todos"}
                </button>
                {cats.map(c => (
                  <button key={c} onClick={() => setFilterCat(filterCat === c ? "" : c)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition-all shrink-0 ${filterCat === c ? "bg-zinc-900 text-white" : "bg-white text-zinc-500 border border-zinc-100"}`}
                    data-testid={`filter-${c}`}>
                    {c}
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Filtered Places — by category, horizontal scroll */}
          {places.length > 0 && (() => {
            const filtered = places.filter(p => {
              const matchSearch = !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.category?.toLowerCase().includes(searchQuery.toLowerCase()) || p.why_go?.toLowerCase().includes(searchQuery.toLowerCase());
              const matchCat = !filterCat || p.category === filterCat;
              return matchSearch && matchCat;
            });
            if (filtered.length === 0) return (
              <div className="text-center py-8 mb-6"><Search className="w-8 h-8 mx-auto mb-2 text-zinc-200" /><p className="text-xs text-zinc-400">{lang === "it" ? "Nessun risultato" : "No results"}</p></div>
            );

            // Group by category
            const cats = [...new Set(filtered.map(p => p.category).filter(Boolean))];
            const uncategorized = filtered.filter(p => !p.category);

            return (
              <div className="mb-8 space-y-6">
                {cats.map(cat => {
                  const catPlaces = filtered.filter(p => p.category === cat);
                  const tone = pastelForCategory(cat);
                  return (
                    <div key={cat}>
                      <p className="text-[15px] font-bold text-zinc-900 mb-3 flex items-center gap-2 tracking-tight">
                        <Compass className="w-4 h-4" style={{ color: tone.icon }} /> {cat}
                      </p>
                      <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory pl-1 pr-4" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
                        {catPlaces.map((p, i) => <PlaceCard key={p.id || i} place={p} index={i} propertyName={config.property_name || ""} t={t} onClick={() => setSelectedPlace(p)} pastel={tone} />)}
                        <div className="shrink-0 w-1" />
                      </div>
                    </div>
                  );
                })}
                {uncategorized.length > 0 && (
                  <div>
                    <p className="text-[15px] font-bold text-zinc-900 mb-3 flex items-center gap-2 tracking-tight">
                      <MapPin className="w-4 h-4 text-emerald-500" /> {t.recommendedPlaces}
                    </p>
                    <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory pl-1 pr-4" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
                      {uncategorized.map((p, i) => <PlaceCard key={p.id || i} place={p} index={i} propertyName={config.property_name || ""} t={t} onClick={() => setSelectedPlace(p)} />)}
                      <div className="shrink-0 w-1" />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* GetYourGuide Experiences */}
          {config.gyg_enabled && config.gyg_location && (
            <GYGCarousel location={config.gyg_location} lang={lang} />
          )}

          {!places.length && !services.length && (
            <div className="text-center py-16"><Compass className="w-10 h-10 mx-auto mb-3 text-zinc-200" /><p className="text-sm text-zinc-400">{t.noContent}</p></div>
          )}
        </motion.div>
      )}

      {/* ═══ RULES ═══ */}
      <TicketSheet open={openSheet === "rules"} onClose={() => setOpenSheet(null)} testId="sheet-rules" z={1200}>
          <div className="tks-heading" style={{ "--tk-accent": tgAccent(2, "#8B5CF6") }}>
            <div className="h-ic" style={{ background: "linear-gradient(135deg,#A78BFA 0%,#8B5CF6 55%,#5B21B6 100%)" }}><Clock className="w-5 h-5" /></div>
            <div>
              <h2>{t.houseRules}</h2>
              <p>{lang === "it" ? "Tutto quello che c'è da sapere" : lang === "en" ? "Everything you need to know" : lang === "fr" ? "Tout ce qu'il faut savoir" : lang === "es" ? "Todo lo que debes saber" : "Alles Wissenswerte"}</p>
            </div>
          </div>
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }} className="space-y-3">
            {/* Check-in / Check-out */}
            {(houseRules?.checkin_time || houseRules?.checkout_time) && (
              <motion.div variants={cardIn} custom={0} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center"><Clock className="w-6 h-6 text-sky-500" /></motion.div>
                  <p className="font-extrabold text-zinc-900 text-base tracking-tight">{t.checkin} / {t.checkout}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {houseRules?.checkin_time && (
                    <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                      <p className="text-[11px] font-extrabold uppercase tracking-wide text-emerald-600 mb-1">{t.checkin}</p>
                      <p className="text-2xl font-black text-emerald-700">{houseRules.checkin_time}</p>
                    </div>
                  )}
                  {houseRules?.checkout_time && (
                    <div className="bg-red-50 rounded-2xl p-4 text-center">
                      <p className="text-[11px] font-extrabold uppercase tracking-wide text-red-500 mb-1">{t.checkout}</p>
                      <p className="text-2xl font-black text-red-600">{houseRules.checkout_time}</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Quiet Hours */}
            {(houseRules?.quiet_from || houseRules?.quiet_to) && (
              <motion.div variants={cardIn} custom={1} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-violet-50 rounded-2xl flex items-center justify-center"><Volume2 className="w-6 h-6 text-violet-500" /></motion.div>
                  <div>
                    <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{t.quietHours}</p>
                    <p className="text-sm text-zinc-500 mt-0.5">
                      {t.from} <span className="font-bold text-violet-600">{houseRules.quiet_from}</span> {t.to} <span className="font-bold text-violet-600">{houseRules.quiet_to}</span>
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Where to leave key */}
            {houseRules?.key_instructions && (
              <motion.div variants={cardIn} custom={2} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4 mb-3">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-amber-50 rounded-2xl flex items-center justify-center"><Key className="w-6 h-6 text-amber-500" /></motion.div>
                  <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{t.whereToLeaveKey}</p>
                </div>
                <p className="text-[15px] text-zinc-600 leading-relaxed whitespace-pre-line">{houseRules.key_instructions}</p>
              </motion.div>
            )}

            {/* Waste Schedule (duplicated here for Rules context) */}
            {wasteMap.length > 0 && (
              <motion.div variants={cardIn} custom={3} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center"><Trash2 className="w-6 h-6 text-emerald-500" /></motion.div>
                  <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{t.wasteCollection}</p>
                </div>
                <div className="space-y-2">
                  {wasteMap.map((w, i) => (
                    <div key={i} className="flex items-center gap-3 bg-zinc-50 rounded-xl p-3">
                      <div className="w-4 h-4 rounded-full shrink-0" style={{ background: w.color || "#94A3B8" }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-zinc-900">{w.type}</p>
                        <p className="text-[10px] text-zinc-400">{w.days}{w.notes ? ` — ${w.notes}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Tourist Tax */}
            {houseRules?.tourist_tax_info && (
              <motion.div variants={cardIn} custom={4} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4 mb-3">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center"><CreditCard className="w-6 h-6 text-sky-500" /></motion.div>
                  <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{t.touristTax}</p>
                </div>
                <p className="text-[15px] text-zinc-600 leading-relaxed whitespace-pre-line mb-3">{houseRules.tourist_tax_info}</p>
                {houseRules?.tourist_tax_link && (
                  <motion.a href={houseRules.tourist_tax_link} target="_blank" rel="noopener noreferrer"
                    whileTap={{ scale: 0.96 }}
                    className="block w-full bg-emerald-500 hover:bg-emerald-600 text-white rounded-2xl py-3.5 font-bold text-sm text-center transition-colors flex items-center justify-center gap-2"
                    data-testid="pay-tax-btn">
                    <CreditCard className="w-5 h-5" /> {t.payNow}
                  </motion.a>
                )}
              </motion.div>
            )}

            {/* Custom Rules */}
            {houseRules?.custom_rules?.length > 0 && (
              <motion.div variants={cardIn} custom={5} className="bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                <div className="flex items-center gap-4 mb-4">
                  <motion.div animate={iconPulse} className="w-12 h-12 bg-zinc-100 rounded-2xl flex items-center justify-center"><Info className="w-6 h-6 text-zinc-500" /></motion.div>
                  <p className="font-extrabold text-zinc-900 text-[15px] tracking-tight">{t.houseRules}</p>
                </div>
                <ul className="space-y-2">
                  {houseRules.custom_rules.filter(Boolean).map((rule, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-zinc-600">
                      <span className="text-zinc-300 font-bold mt-0.5">{i + 1}.</span>
                      <span className="leading-relaxed">{rule}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </motion.div>
      </TicketSheet>

      {/* ═══ TOILETS / SERVIZI IGIENICI ═══ */}
      {tab === "toilets" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100" data-testid="toilets-back-btn"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <div>
              <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight">{lang === "it" ? "Servizi Igienici" : lang === "en" ? "Public Toilets" : lang === "fr" ? "Toilettes" : lang === "es" ? "Aseos" : "Toiletten"}</h2>
              <p className="text-[11px] text-zinc-400 font-medium">{lang === "it" ? "Bagni pubblici nelle vicinanze" : lang === "en" ? "Restrooms nearby" : lang === "fr" ? "Toilettes à proximité" : lang === "es" ? "Baños cercanos" : "Toiletten in der Nähe"}</p>
            </div>
          </div>
          <ToiletsMap
            lat={config.toilets_lat || config.location_lat}
            lng={config.toilets_lng || config.location_lng}
            locationLabel={config.toilets_label || config.weather_label || config.property_address}
            lang={lang}
          />
        </motion.div>
      )}

      {/* ═══ HELP ═══ */}
      {tab === "help" && (
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", damping: 26, stiffness: 260 }} className="pt-16 px-6">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => setTab("home")} className="p-2.5 bg-white rounded-2xl shadow-sm border border-zinc-100"><ChevronRight className="w-5 h-5 text-zinc-400 rotate-180" /></button>
            <h2 className="text-[28px] font-extrabold text-zinc-900 tracking-tight"><Typewriter text={t.help} /></h2>
          </div>

          {(config.emergency?.host_phone || config.emergency?.host_whatsapp) && (
            <div className="mb-6">
              <p className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">{t.contactHost}</p>
              <div className="space-y-3">
                {config.emergency?.host_phone && (
                  <a href={`tel:${config.emergency.host_phone}`} className="flex items-center gap-4 bg-white rounded-3xl p-5 shadow-sm border border-zinc-100">
                    <motion.div animate={iconPulse} className="w-12 h-12 bg-sky-50 rounded-2xl flex items-center justify-center"><Phone className="w-6 h-6 text-sky-500" /></motion.div>
                    <div><p className="font-bold text-zinc-900 text-sm">{t.call}</p><p className="text-xs text-zinc-400">{config.emergency.host_phone}</p></div>
                  </a>
                )}
                {config.emergency?.host_whatsapp && (
                  <a href={`https://wa.me/${config.emergency.host_whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-white rounded-3xl p-5 shadow-sm border border-emerald-100">
                    <motion.div animate={iconPulse} className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center"><MessageCircle className="w-6 h-6 text-emerald-500" /></motion.div>
                    <div><p className="font-bold text-zinc-900 text-sm">WhatsApp</p><p className="text-xs text-zinc-400">{config.emergency.host_whatsapp}</p></div>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Veterinario & Farmacia */}
          {(config.emergency?.vet_phone || config.emergency?.vet_maps || config.emergency?.pharmacy_phone || config.emergency?.pharmacy_maps) && (
            <div className="mb-6 space-y-3">
              {(config.emergency?.vet_phone || config.emergency?.vet_maps) && (
                <div className="bg-white rounded-3xl p-4 shadow-sm border border-zinc-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center relative">
                      <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none">
                        <path d="M4.5 9.5a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm5-3a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm5 0a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm5 3a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" fill="#F43F5E"/>
                        <path d="M12 21s-7-5.5-7-10a4.5 4.5 0 0 1 7-3.5A4.5 4.5 0 0 1 19 11c0 4.5-7 10-7 10z" fill="#F43F5E" opacity="0.7"/>
                      </svg>
                    </div>
                    <p className="font-bold text-zinc-900 text-sm">{lang === "it" ? "Veterinario" : lang === "fr" ? "Vétérinaire" : lang === "es" ? "Veterinario" : "Veterinarian"}</p>
                  </div>
                  <div className="flex gap-2">
                    {config.emergency.vet_phone && (
                      <a href={`tel:${config.emergency.vet_phone}`} className="flex-1 flex items-center justify-center gap-1.5 bg-rose-500 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="vet-call">
                        <Phone className="w-3.5 h-3.5" /> {lang === "it" ? "Chiama" : "Call"}
                      </a>
                    )}
                    {config.emergency.vet_maps && (
                      <a href={config.emergency.vet_maps} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1.5 bg-zinc-900 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="vet-maps">
                        <MapPin className="w-3.5 h-3.5" /> Maps
                      </a>
                    )}
                  </div>
                </div>
              )}
              {(config.emergency?.pharmacy_phone || config.emergency?.pharmacy_maps) && (
                <div className="bg-white rounded-3xl p-4 shadow-sm border border-zinc-100">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-emerald-500" /></div>
                    <p className="font-bold text-zinc-900 text-sm">{lang === "it" ? "Farmacia piu vicina" : lang === "fr" ? "Pharmacie" : lang === "es" ? "Farmacia" : "Nearest Pharmacy"}</p>
                  </div>
                  <div className="flex gap-2">
                    {config.emergency.pharmacy_phone && (
                      <a href={`tel:${config.emergency.pharmacy_phone}`} className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="pharmacy-call">
                        <Phone className="w-3.5 h-3.5" /> {lang === "it" ? "Chiama" : "Call"}
                      </a>
                    )}
                    {config.emergency.pharmacy_maps && (
                      <a href={config.emergency.pharmacy_maps} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-1.5 bg-zinc-900 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="pharmacy-maps">
                        <MapPin className="w-3.5 h-3.5" /> Maps
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-red-400 mb-3">{t.emergencies}</p>
            {config.emergency?.address_for_emergency && (
              <div className="bg-red-50 border border-red-200 rounded-3xl p-5 mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider text-red-400 mb-1">{t.emergencyAddress}</p>
                <p className="text-sm font-bold text-zinc-900">{config.emergency.address_for_emergency}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {EMERGENCY.map((c, i) => (
                <motion.a key={i} href={`tel:${c.num}`} whileTap={{ scale: 0.95 }}
                  className="bg-white rounded-3xl p-4 flex items-center gap-3 shadow-sm border border-zinc-100">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: c.color }}><Phone className="w-5 h-5 text-white" /></div>
                  <div><p className="text-[10px] font-bold text-zinc-500">{c.name}</p><p className="text-lg font-black" style={{ color: c.color }}>{c.num}</p></div>
                </motion.a>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* ═══ PLACE DETAIL FULLSCREEN ═══ */}
      <AnimatePresence>
        {selectedPlace && (
          <PlaceDetailSheet
            place={selectedPlace}
            propertyName={config.property_name || ""}
            propertyLat={config.location_lat}
            propertyLng={config.location_lng}
            propertyCity={config.property_city}
            lang={lang}
            onClose={() => setSelectedPlace(null)}
          />
        )}
      </AnimatePresence>

      {/* ═══ STORIES VIEWER (Scopri la Zona) ═══ */}
      <AnimatePresence>
        {storyOpen && (
          <StoriesViewer
            stories={storyOpen.stories}
            startIndex={0}
            originId={storyOpen.originId}
            lang={lang}
            propertyId={propertyId}
            onSeen={() => setSeenVersion((v) => v + 1)}
            onClose={() => { setStoryOpen(null); setSeenVersion((v) => v + 1); }}
          />
        )}
      </AnimatePresence>

      {/* ═══ BOTTOM SHEETS — Check-in / Wi-Fi / Manuali ═══ */}

      {/* Lightbox foto check-in */}
      <AnimatePresence>
        {lightbox !== null && (checkin.photos || [])[lightbox] && (
          <motion.div className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)} data-testid="checkin-lightbox">
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white z-10 transition-colors" data-testid="lightbox-close" aria-label="Chiudi"><X className="w-5 h-5" /></button>
            <motion.img key={lightbox} src={fileSrc(checkin.photos[lightbox])} alt=""
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 300, damping: 28 }}
              className="max-w-[92vw] max-h-[82vh] object-contain rounded-2xl" onClick={(e) => e.stopPropagation()} />
            {checkin.photos.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + checkin.photos.length) % checkin.photos.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors" aria-label="Precedente"><ChevronLeft className="w-5 h-5" /></button>
                <button onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % checkin.photos.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-colors" aria-label="Successiva"><ChevronRight className="w-5 h-5" /></button>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs font-medium">{lightbox + 1} / {checkin.photos.length}</div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Guida dettagliata — lettore PDF / download Word in bottom-sheet */}
      <BottomSheet
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        z={120}
        title={lang === "it" ? "Guida dettagliata" : lang === "fr" ? "Guide détaillé" : lang === "es" ? "Guía detallada" : lang === "de" ? "Detaillierte Anleitung" : "Detailed guide"}
        accent="#0EA5E9"
        icon={FileText}
        testId="sheet-guide"
      >
        {(() => {
          const gf = checkin.guide_file;
          if (!gf) return null;
          const url = (gf.startsWith("http") || gf.startsWith("/")) ? gf : `${API}/files/${gf}`;
          const isPdf = /\.pdf($|\?)/i.test(gf);
          if (isPdf) {
            return <iframe src={url} title="Guida dettagliata" className="w-full rounded-2xl border border-zinc-100 bg-white" style={{ height: "72vh" }} data-testid="guide-pdf-viewer" />;
          }
          return (
            <div className="text-center py-10" data-testid="guide-download">
              <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-3" />
              <p className="text-sm text-zinc-500 mb-5">{lang === "it" ? "Documento pronto da scaricare" : "Document ready to download"}</p>
              <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-white font-bold text-sm">
                <Download className="w-4 h-4" /> {lang === "it" ? "Scarica la guida" : lang === "fr" ? "Télécharger" : lang === "es" ? "Descargar" : lang === "de" ? "Herunterladen" : "Download"}
              </a>
            </div>
          );
        })()}
      </BottomSheet>

      <TicketSheet
        open={openSheet === "checkin"}
        onClose={() => setOpenSheet(null)}
        testId="sheet-checkin"
      >
            {/* ═══ CHECK-IN INSTRUCTIONS ═══ */}
            {checkin && (checkin.precheckin_url || checkin.directions || checkin.location_url || checkin.self_checkin_instructions || checkin.keybox_code || checkin.keybox_internal || checkin.door_link || checkin.door_link_internal || checkin.video_url || checkin.extra_notes || checkin.checkin_time || checkin.help_phone || (Array.isArray(checkin.parking) && checkin.parking.length > 0)) && (
              <motion.div variants={cardIn} custom={1} initial="hidden" animate="visible"
                className="tk" style={{ "--tk-accent": "#ef2b1d", "--tk-accent-soft": "rgba(200,18,8,.18)" }} data-testid="checkin-section">
                <div className="tk-checker" />
                <div className="tk-head tk-head-vibrant" style={{ background: "linear-gradient(135deg,#ff4d2e 0%,#ef2b1d 48%,#8f0d06 100%)" }}>
                  <div className="tk-head-row">
                    <div className="tk-ic"><Key className="w-5 h-5" strokeWidth={2.4} /></div>
                    <div>
                      <div className="tk-kicker"><span className="dot" />{lang === "it" ? "ACCESSO STRUTTURA" : lang === "fr" ? "ACCÈS LOGEMENT" : lang === "es" ? "ACCESO ALOJAMIENTO" : lang === "de" ? "ZUGANG" : "PROPERTY ACCESS"}</div>
                      <div className="tk-title">Check-in</div>
                    </div>
                  </div>
                  {checkin.checkin_time && <div className="tk-sub">🕐 {checkin.checkin_time}</div>}
                </div>
                <div className="tk-perf"><span /></div>

                <div className="tk-body space-y-4">
                  {/* Pre Check-in — animated document icon */}
                  {checkin.precheckin_url && (
                    <motion.a href={checkin.precheckin_url} target="_blank" rel="noopener noreferrer"
                      whileTap={{ scale: 0.97 }}
                      className="flex items-center gap-4 bg-red-500 text-white rounded-2xl px-4 py-3.5 shadow-lg shadow-red-500/20 group" data-testid="checkin-precheckin-btn">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0 relative overflow-hidden">
                        <motion.div animate={{ y: [0, -2, 0], rotate: [0, 3, -3, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}>
                          <BookOpen className="w-6 h-6" />
                        </motion.div>
                        <motion.div animate={{ scale: [0, 1, 0] }} transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                          className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-white rounded-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm">{lang === "it" ? "Pre Check-in Online" : lang === "fr" ? "Pré check-in en ligne" : lang === "es" ? "Pre check-in online" : "Online Pre Check-in"}</p>
                        <p className="text-white/70 text-[11px] mt-0.5">{lang === "it" ? "Completa il documento obbligatorio" : "Complete the required document"}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 shrink-0 opacity-60 group-hover:translate-x-1 transition-transform" />
                    </motion.a>
                  )}

                  {/* Directions */}
                  {checkin.directions && (
                    <div>
                      <SectionLabel color="#ef2b1d">
                        {lang === "it" ? "Come arrivare" : lang === "fr" ? "Comment arriver" : lang === "es" ? "Cómo llegar" : "How to get there"}
                      </SectionLabel>
                      <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{checkin.directions}</p>
                    </div>
                  )}

                  {/* Location — mini map + button */}
                  {checkin.location_url && (() => {
                    const url = checkin.location_url;
                    const coordMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                    const placeMatch = url.match(/place\/([^/@]+)/);
                    const qMatch = url.match(/[?&]q=([^&]+)/);
                    let mapQuery = "";
                    if (coordMatch) mapQuery = `${coordMatch[1]},${coordMatch[2]}`;
                    else if (placeMatch) mapQuery = decodeURIComponent(placeMatch[1].replace(/\+/g, " "));
                    else if (qMatch) mapQuery = decodeURIComponent(qMatch[1]);

                    return (
                      <div className="rounded-2xl overflow-hidden border border-zinc-200 shadow-sm">
                        {mapQuery && (
                          <div className="w-full h-36 relative">
                            <iframe
                              src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                              className="w-full h-full border-0 grayscale contrast-[1.1]" loading="lazy" title="Map" />
                          </div>
                        )}
                        <motion.a href={url} target="_blank" rel="noopener noreferrer"
                          whileTap={{ scale: 0.97 }}
                          className="flex items-center gap-4 bg-zinc-900 text-white px-4 py-3.5 group" data-testid="checkin-location-btn">
                          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center shrink-0 relative">
                            <MapPin className="w-5 h-5" />
                            <motion.div animate={{ x: [0, 3, -2, 1, 0], y: [0, -2, 1, -1, 0] }} transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                              className="absolute top-0.5 right-0.5">
                              <Search className="w-3 h-3 opacity-60" />
                            </motion.div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{lang === "it" ? "Posizione esatta struttura" : lang === "fr" ? "Position exacte" : lang === "es" ? "Ubicación exacta" : "Exact location"}</p>
                            <p className="text-white/60 text-[11px] mt-0.5">{lang === "it" ? "Apri in Google Maps" : "Open in Google Maps"}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 shrink-0 opacity-40 group-hover:translate-x-1 transition-transform" />
                        </motion.a>
                      </div>
                    );
                  })()}

                  {/* Where to park — list of blue pill buttons.
                      Only shown when the host has added at least one parking. Each tap
                      opens the parking's Google Maps URL in a new tab. */}
                  {Array.isArray(checkin.parking) && checkin.parking.length > 0 && (
                    <div data-testid="checkin-parking-section">
                      <SectionLabel color="#ef2b1d">
                        {lang === "it" ? "Dove parcheggiare" : lang === "fr" ? "Où se garer" : lang === "es" ? "Dónde aparcar" : "Where to park"}
                      </SectionLabel>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {checkin.parking.filter((p) => p.google_maps_url || p.name).map((p, idx) => (
                          <motion.a
                            key={idx}
                            href={p.google_maps_url || "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                            whileTap={{ scale: 0.96 }}
                            whileHover={{ y: -2 }}
                            className="flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl px-3 py-3 shadow-lg shadow-blue-600/20 transition-colors"
                            data-testid={`parking-btn-${idx}`}
                          >
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0 font-black text-lg">P</div>
                            <div className="flex-1 min-w-0">
                              <p className="font-extrabold text-[15px] tracking-tight leading-tight truncate">{p.name || (lang === "it" ? "Parcheggio" : "Parking")}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {p.walking_minutes ? (
                                  <span className="text-white/80 text-[10px] font-semibold">🚶 {p.walking_minutes} min</span>
                                ) : null}
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${p.payment === "paid" ? "bg-amber-300/30 text-amber-100" : "bg-emerald-300/30 text-emerald-100"}`}>
                                  {p.payment === "paid"
                                    ? (lang === "it" ? "A pagamento" : lang === "fr" ? "Payant" : lang === "es" ? "De pago" : "Paid")
                                    : (lang === "it" ? "Gratis" : "Free")}
                                </span>
                              </div>
                            </div>
                          </motion.a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Self check-in instructions */}
                  {checkin.self_checkin_instructions && (
                    <div>
                      <SectionLabel color="#ef2b1d">
                        {lang === "it" ? "Istruzioni Self Check-in" : "Self Check-in Instructions"}
                      </SectionLabel>
                      <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{checkin.self_checkin_instructions}</p>
                    </div>
                  )}

                  {/* Photos horizontal scroll — tap per ingrandire (lightbox) */}
                  {(checkin.photos || []).length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory" style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
                      {checkin.photos.map((ph, idx) => (
                        <img key={idx} src={fileSrc(ph)} alt="" onClick={() => { vibrate(10); setLightbox(idx); }} className="h-28 w-auto rounded-2xl object-cover shrink-0 snap-start shadow-sm border border-zinc-100 cursor-pointer active:scale-95 transition-transform" data-testid={`checkin-photo-${idx}`} />
                      ))}
                    </div>
                  )}

                  {/* Guida dettagliata (PDF/Word) — apre lettore in bottom-sheet */}
                  {checkin.guide_file && (
                    <button onClick={() => { vibrate(10); setGuideOpen(true); }} data-testid="checkin-guide-btn"
                      className="w-full flex items-center gap-4 bg-zinc-900 text-white rounded-2xl px-4 py-3.5 shadow-lg">
                      <div className="w-12 h-12 bg-white/15 rounded-xl flex items-center justify-center shrink-0"><FileText className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0 text-left">
                        <p className="font-bold text-sm">{lang === "it" ? "Guida dettagliata" : lang === "fr" ? "Guide détaillé" : lang === "es" ? "Guía detallada" : lang === "de" ? "Detaillierte Anleitung" : "Detailed guide"}</p>
                        <p className="text-white/60 text-[11px] mt-0.5">{lang === "it" ? "Tocca per aprire" : "Tap to open"}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 shrink-0 opacity-60" />
                    </button>
                  )}

                  {/* Door links — animated finger press icon */}
                  {(checkin.door_link || checkin.door_link_internal) && (
                    <div className="space-y-2.5">
                      {checkin.door_link && (
                        <motion.a href={checkin.door_link} target="_blank" rel="noopener noreferrer"
                          whileTap={{ scale: 0.97 }}
                          className="flex items-center gap-4 bg-emerald-500 text-white rounded-2xl px-4 py-3.5 shadow-lg shadow-emerald-500/20 group" data-testid="checkin-door-btn">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0 relative">
                            <Lock className="w-5 h-5" />
                            <motion.div animate={{ y: [0, 2, -1, 0], scale: [1, 0.9, 1.05, 1] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1 }}
                              className="absolute -bottom-0.5 -right-0.5">
                              <Zap className="w-3.5 h-3.5 text-amber-300" />
                            </motion.div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{lang === "it" ? "Apri porta esterna" : "Open external door"}</p>
                            <p className="text-white/70 text-[11px] mt-0.5">{lang === "it" ? "Tocca per sbloccare" : "Tap to unlock"}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 shrink-0 opacity-60 group-hover:translate-x-1 transition-transform" />
                        </motion.a>
                      )}
                      {checkin.door_link_internal && (
                        <motion.a href={checkin.door_link_internal} target="_blank" rel="noopener noreferrer"
                          whileTap={{ scale: 0.97 }}
                          className="flex items-center gap-4 bg-sky-500 text-white rounded-2xl px-4 py-3.5 shadow-lg shadow-sky-500/20 group" data-testid="checkin-door-internal-btn">
                          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center shrink-0 relative">
                            <Lock className="w-5 h-5" />
                            <motion.div animate={{ y: [0, 2, -1, 0], scale: [1, 0.9, 1.05, 1] }} transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 1.5 }}
                              className="absolute -bottom-0.5 -right-0.5">
                              <Zap className="w-3.5 h-3.5 text-amber-300" />
                            </motion.div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm">{lang === "it" ? "Apri porta interna" : "Open internal door"}</p>
                            <p className="text-white/70 text-[11px] mt-0.5">{lang === "it" ? "Tocca per sbloccare" : "Tap to unlock"}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 shrink-0 opacity-40 group-hover:translate-x-1 transition-transform" />
                        </motion.a>
                      )}
                    </div>
                  )}

                  {/* Keybox codes */}
                  {(checkin.keybox_code || checkin.keybox_internal) && (
                    <div className="space-y-2.5">
                      {checkin.keybox_code && (
                        <div className="tk-codebox" style={{ "--tk-accent": "#fbbf24", "--tk-accent-soft": "rgba(251,191,36,.35)" }}>
                          <div className="min-w-0">
                            <div className="cb-lbl">{lang === "it" ? "KeyBox Esterna" : lang === "fr" ? "KeyBox Externe" : lang === "es" ? "KeyBox Externa" : lang === "de" ? "KeyBox Außen" : "External KeyBox"}</div>
                            <div className="cb-val">{checkin.keybox_code}</div>
                          </div>
                          <button className="tk-copy" onClick={() => { navigator.clipboard?.writeText(checkin.keybox_code); toast.success(lang === "it" ? "Codice copiato!" : "Code copied!"); }} data-testid="checkin-copy-keybox" aria-label="Copy">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      {checkin.keybox_internal && (
                        <div className="tk-codebox" style={{ "--tk-accent": "#ef2b1d", "--tk-accent-soft": "rgba(200,18,8,.20)" }}>
                          <div className="min-w-0">
                            <div className="cb-lbl">{lang === "it" ? "KeyBox Interna" : lang === "fr" ? "KeyBox Interne" : lang === "es" ? "KeyBox Interna" : lang === "de" ? "KeyBox Innen" : "Internal KeyBox"}</div>
                            <div className="cb-val">{checkin.keybox_internal}</div>
                          </div>
                          <button className="tk-copy" onClick={() => { navigator.clipboard?.writeText(checkin.keybox_internal); toast.success(lang === "it" ? "Codice copiato!" : "Code copied!"); }} aria-label="Copy">
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Video — supporta YouTube / Vimeo / Google Drive, coerente
                      con il resto dello stack video del guestbook. */}
                  {checkin.video_url && (() => {
                    const parsed = parseVideoUrl(checkin.video_url);
                    if (!parsed || parsed.kind === "mp4") return null;
                    return (
                      <div>
                        <SectionLabel color="#ef2b1d">
                          {lang === "it" ? "Video tutorial" : "Video tutorial"}
                        </SectionLabel>
                        <div className="rounded-2xl overflow-hidden border border-zinc-100">
                          <iframe
                            src={parsed.embedUrl}
                            className="w-full aspect-video" frameBorder="0"
                            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
                            title="Check-in video" />
                        </div>
                      </div>
                    );
                  })()}

                  {/* Extra notes */}
                  {checkin.extra_notes && (
                    <div className="bg-zinc-50 rounded-2xl p-4">
                      <SectionLabel color="#71717A">
                        {lang === "it" ? "Note" : "Notes"}
                      </SectionLabel>
                      <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{checkin.extra_notes}</p>
                    </div>
                  )}

                  {/* Help phone — call + WhatsApp. Same tone as the section header
                      so it doesn't stand out with a random yellow box. */}
                  {checkin.help_phone && (
                    <div className="bg-red-50/70 border border-red-100 rounded-2xl p-4">
                      <SectionLabel color="#ef2b1d">
                        {lang === "it" ? "Hai bisogno di aiuto?" : lang === "fr" ? "Besoin d'aide ?" : lang === "es" ? "¿Necesitas ayuda?" : "Need help?"}
                      </SectionLabel>
                      <div className="flex gap-2">
                        <a href={`tel:+39${checkin.help_phone}`}
                          className="flex-1 flex items-center justify-center gap-2 bg-zinc-900 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="checkin-call-help">
                          <Phone className="w-3.5 h-3.5" /> {lang === "it" ? "Chiama" : "Call"}
                        </a>
                        <a href={`https://wa.me/39${checkin.help_phone}?text=${encodeURIComponent(lang === "it" ? "Ciao, ho bisogno di aiuto per il check-in" : "Hi, I need help with the check-in")}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-2 bg-emerald-500 text-white rounded-xl py-2.5 font-bold text-xs" data-testid="checkin-wa-help">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
      </TicketSheet>

      <TicketSheet
        open={openSheet === "wifi"}
        onClose={() => setOpenSheet(null)}
        testId="sheet-wifi"
      >
            {/* Wi-Fi */}
            <motion.div initial="hidden" animate="visible">
              <WifiCard wifi={config.wifi} t={t} lang={lang} accent="#334155" gradient="linear-gradient(135deg,#475569 0%,#1F2937 55%,#0F172A 100%)" />
            </motion.div>
      </TicketSheet>

      <TicketSheet
        open={openSheet === "manuals"}
        onClose={() => setOpenSheet(null)}
        testId="sheet-manuals"
      >
            {/* Manuali della casa — stile ticket coerente con Check-in/Wi-Fi/Meteo/Regole */}
            {manuals.length > 0 && (
              <motion.div variants={cardIn} custom={3} initial="hidden" animate="visible"
                className="tk" style={{ "--tk-accent": tgAccent(1, "#F43F5E"), "--tk-accent-soft": `${tgAccent(1, "#F43F5E")}2e` }}>
                <div className="tk-checker" />
                <div className="tk-head tk-head-vibrant" style={{ background: tg(1, "linear-gradient(135deg,#FB7185 0%,#F43F5E 55%,#BE123C 100%)") }}>
                  <div className="tk-head-row">
                    <div className="tk-ic"><BookOpen className="w-5 h-5" strokeWidth={2.4} /></div>
                    <div>
                      <div className="tk-kicker"><span className="dot" />{manuals.length} {lang === "it" ? "ISTRUZIONI" : lang === "fr" ? "INSTRUCTIONS" : lang === "es" ? "INSTRUCCIONES" : lang === "de" ? "ANLEITUNGEN" : "INSTRUCTIONS"}</div>
                      <div className="tk-title">{t.homeManuals}</div>
                    </div>
                  </div>
                </div>
                <div className="tk-perf"><span /></div>
                <div className="tk-body space-y-2.5">
                  {manuals.map((m, i) => (
                    <HomeManualItem key={i} m={m} lang={lang} index={i} />
                  ))}
                </div>
              </motion.div>
            )}
      </TicketSheet>

      <TicketSheet
        open={openSheet === "useful"}
        onClose={() => setOpenSheet(null)}
        testId="sheet-useful"
      >
        <motion.div variants={cardIn} custom={1} initial="hidden" animate="visible"
          className="tk" style={{ "--tk-accent": tgAccent(8, "#334155"), "--tk-accent-soft": `${tgAccent(8, "#334155")}2e` }} data-testid="useful-section">
          <div className="tk-checker" />
          <div className="tk-head tk-head-vibrant" style={{ background: tg(8, "linear-gradient(135deg,#475569 0%,#1F2937 55%,#0F172A 100%)") }}>
            <div className="tk-head-row">
              <div className="tk-ic"><Phone className="w-5 h-5" strokeWidth={2.4} /></div>
              <div>
                <div className="tk-kicker"><span className="dot" />{lang === "it" ? "CONTATTI ZONA" : lang === "fr" ? "CONTACTS LOCAUX" : lang === "es" ? "CONTACTOS ZONA" : lang === "de" ? "KONTAKTE" : "LOCAL CONTACTS"}</div>
                <div className="tk-title">{lang === "it" ? "Numeri Utili" : lang === "fr" ? "Numéros Utiles" : lang === "es" ? "Números Útiles" : lang === "de" ? "Nützliche Nummern" : "Useful Numbers"}</div>
              </div>
            </div>
            <div className="tk-sub">{lang === "it" ? "Tutti i contatti della zona a portata di tap" : lang === "fr" ? "Tous les contacts locaux à portée de doigt" : lang === "es" ? "Todos los contactos locales a un toque" : lang === "de" ? "Alle lokalen Kontakte auf Tap" : "All local contacts at your fingertips"}</div>
          </div>
          <div className="tk-perf"><span /></div>

          <div className="tk-body">
            <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              className="grid grid-cols-2 gap-3 items-stretch">
              {(config.useful_numbers || []).map((svc, i) => {
                const meta = USEFUL_ICON_MAP[svc.id] || { icon: Phone, color: "#6B7280" };
                const Icon = meta.icon;
                return (
                  <motion.div key={svc.id}
                    variants={cardIn} custom={i}
                    whileHover={{ y: -4 }}
                    className="relative h-full flex flex-col rounded-[22px] p-3.5 bg-white border border-zinc-100 overflow-hidden"
                    style={{ boxShadow: `0 12px 28px -14px ${meta.color}80, 0 4px 10px -6px rgba(0,0,0,0.12)` }}
                    data-testid={`useful-card-${svc.id}`}
                  >
                    {/* soft colored glow */}
                    <motion.div aria-hidden
                      animate={{ scale: [1, 1.25, 1], opacity: [0.12, 0.2, 0.12] }}
                      transition={{ duration: 3.5, repeat: Infinity, delay: i * 0.2 }}
                      className="absolute -top-7 -right-7 w-24 h-24 rounded-full pointer-events-none"
                      style={{ background: meta.color }} />
                    {/* big glossy icon */}
                    <motion.div
                      whileHover={{ rotate: [0, -6, 6, 0] }}
                      transition={{ duration: 0.6 }}
                      className="relative w-14 h-14 rounded-[18px] flex items-center justify-center mb-2.5 shrink-0 overflow-hidden"
                      style={{ background: meta.color, boxShadow: `0 10px 20px -6px ${meta.color}99` }}
                    >
                      <span aria-hidden className="absolute inset-0 pointer-events-none"
                        style={{ background: "linear-gradient(160deg,rgba(255,255,255,0.45) 0%,rgba(255,255,255,0) 55%)" }} />
                      <Icon className="relative w-7 h-7 text-white" strokeWidth={2.1} />
                    </motion.div>
                    {/* title */}
                    <p className="font-black text-zinc-900 text-[15px] leading-[1.15] tracking-tight mb-3 flex-1">{svc.label}</p>
                    {/* actions — stacked, big tap targets */}
                    <div className="space-y-2 mt-auto">
                      {svc.phone && (
                        <a href={`tel:${svc.phone}`}
                          className="w-full flex items-center justify-center gap-1.5 text-white text-[13px] font-black py-2.5 rounded-xl transition-all active:scale-95"
                          style={{ background: meta.color, boxShadow: `0 8px 16px -8px ${meta.color}` }}
                          data-testid={`useful-call-${svc.id}`}>
                          <Phone className="w-3.5 h-3.5" strokeWidth={2.6} />
                          {lang === "it" ? "Chiama" : lang === "fr" ? "Appeler" : lang === "es" ? "Llamar" : lang === "de" ? "Anrufen" : "Call"}
                        </a>
                      )}
                      {svc.maps_url && (
                        <a href={svc.maps_url} target="_blank" rel="noopener noreferrer"
                          className="w-full flex items-center justify-center gap-1.5 bg-zinc-900 text-white text-[13px] font-black py-2.5 rounded-xl hover:bg-zinc-800 transition-all active:scale-95"
                          data-testid={`useful-maps-${svc.id}`}>
                          <MapPin className="w-3.5 h-3.5" strokeWidth={2.6} /> Maps
                        </a>
                      )}
                      {!svc.phone && !svc.maps_url && (
                        <span className="block text-center text-[11px] text-zinc-300 font-bold py-2.5">{lang === "it" ? "Info non disponibile" : "Not available"}</span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
            {(config.useful_numbers || []).length === 0 && (
              <div className="text-center py-10">
                <Phone className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
                <p className="text-zinc-400 font-bold text-sm">{lang === "it" ? "Nessun numero configurato" : "No numbers configured"}</p>
              </div>
            )}
          </div>
        </motion.div>
      </TicketSheet>

      <TicketSheet
        open={openSheet === "weather"}
        onClose={() => setOpenSheet(null)}
        testId="sheet-weather"
      >
        <WeatherDetail
          lat={config.weather_lat || config.location_lat}
          lng={config.weather_lng || config.location_lng}
          locationLabel={config.weather_label || config.property_address}
          lang={lang}
        />
      </TicketSheet>

      {/* ═══ SOS FLOATING ═══ */}
      <motion.button whileTap={{ scale: 0.9 }} animate={{ scale: [1, 1.05, 1] }} transition={{ duration: 2, repeat: Infinity }}
        onClick={() => setTab("help")}
        className="fixed bottom-24 right-5 z-40 w-14 h-14 rounded-full bg-red-500 shadow-lg shadow-red-500/30 ring-2 ring-black/20 flex flex-col items-center justify-center gap-0.5"
        data-testid="sos-button">
        <Siren className="w-4 h-4 text-white" strokeWidth={2.5} />
        <span className="text-white font-bold text-[13px] tracking-[0.12em] leading-none" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>SOS</span>
      </motion.button>

      {/* ═══ TORNA A CASA 🚕 ═══ */}
      {config.torna_a_casa?.enabled && (
        <TornaACasa data={config.torna_a_casa} lang={lang} preview={new URLSearchParams(window.location.search).get("tac") === "1"} />
      )}

      {/* ═══ BOTTOM NAV ═══ */}
      <BottomNav tabs={tabs} tab={tab} setTab={(id) => (id === "rules" ? setOpenSheet("rules") : setTab(id))} promo={{ show: promoCount > 0, label: promoLabelShort, active: tab === "sconti", onClick: () => setTab("sconti") }} />
    </div>
    </DemoStage>
  );
};

export default GuestbookApp;
